/* eslint-disable jsx-a11y/alt-text */
import "./CallAutomation.css";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DateRangePicker from "../common/dateRangePicker/DateRangePicker";
import Button from "../common/button/Button";
import { useHome } from "../../utils/hooks";
import {
  checkRemainingCalls,
  getAccountDetails,
  pushProviderPatientData,
  validatePatientData,
} from "../../utils/CustomerUiAPI";
import FileUploader from "../common/FileUploader";
import {
  CallAutomationInfo,
  DataValidationOverlay,
} from "../callAutomationInfo";
import { useTheme } from "../../ThemeContext";
import Select from "../common/select/Select";
import { CustomDialog, Search, Popup, Dropdown } from "@voicecare.ai/ui-library";
import UseCaseSelection from "./useCase/UseCaseSelection";
import QuestionSelection from "./useCase/questionSelection/QuestionSelection";
import useCaseOptions from "./useCase/useCaseOptions.json";
import { getProviderTimezone } from "../../utils/timezone-utils";
import { convertToUTC } from "../../utils/date-time-utils";
import ThemedTable, { countErrorCells } from "../common/SimpleTable";
import { formatDateInput } from "../../utils/helpers/format-date-input";
import SimpleDatePicker from "../common/SimpleDatePicker/SimpleDatePicker";
import SimpleTimePicker from "../common/SimpleTimePicker/SimpleTimePicker";
import { ALL_EHRS } from "../../constants/ehr-constants";
import { ROUTES } from "../../config/routeConfig";
import {
  getSlotAvailability,
  getCustomerDetails,
  validatePatientFiles,
  scheduleBatch,
  getColumnHeaders,
  getBatches,
  generateBatchAlias,
  checkBatchAliasUnique,
  getCustomerQuestionUploads,
  fetchEhrPatientsData,
  getEhrEndpointConfig,
  queryEhrData,
  getFacilities,
} from "../../utils/apiClient";
import {
  checkPageAccess,
  checkUnitsAvailability,
  checkTrialStatus,
  TRIAL_USER_PATIENT_LIMIT,
  validateTrialUserLimits,
  getTotalActiveCalls,
} from "../../services/billingRestrictionService";
import BillingRestrictionPopup from "../common/BillingRestrictionPopup";
import HelperButton from "../common/HelperButton";
import { message } from "antd";
import * as XLSX from 'xlsx';
import AIGeneratedText from "../common/animations/AIGeneratedText";
import ThemedTooltip from "../common/ThemedTooltip";
import LogoLoader from "../common/loaders/gif-logo-loader";

const typePriority = { medical: 0, dental: 1 };

const SORTED_EHRS_BY_TYPE = [...ALL_EHRS].sort((a, b) => {
  const aTypeRank = typePriority[a.type] ?? 2;
  const bTypeRank = typePriority[b.type] ?? 2;
  if (aTypeRank !== bTypeRank) return aTypeRank - bTypeRank;
  const aLabel = (a.displayName || a.key || "").toLowerCase();
  const bLabel = (b.displayName || b.key || "").toLowerCase();
  return aLabel.localeCompare(bLabel);
});

const CallAutomation = () => {
  const [isReccuring, setIsReccuring] = useState(false);
  const { themeMode, currentTheme } = useTheme();
  const [batchId, setBatchId] = useState("");
  const [selectedType, setSelectedType] = useState("manual"); // 'manual' | 'API'
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  
  // EHR Configuration states
  const [showEhrConfigPopup, setShowEhrConfigPopup] = useState(false);
  const [selectedEhr, setSelectedEhr] = useState("");
  const [ehrPopupStep, setEhrPopupStep] = useState(1); // 1 = card selection, 2 = config fields
  const [ehrStartDate, setEhrStartDate] = useState(null);
  const [ehrEndDate, setEhrEndDate] = useState(null);
  
  // Add EHR redirect (when user clicks unavailable EHR and chooses to add)
  const [showAddEhrConfirm, setShowAddEhrConfirm] = useState(false);
  const [ehrToAdd, setEhrToAdd] = useState("");

  // Dynamic EHR Configuration
  const [ehrConfig, setEhrConfig] = useState(null);
  const [loadingEhrConfig, setLoadingEhrConfig] = useState(false);
  const [ehrDynamicFields, setEhrDynamicFields] = useState({});
  const [ehrDropdownData, setEhrDropdownData] = useState({});
  const [loadingEhrDropdowns, setLoadingEhrDropdowns] = useState({});
  const [hideUpload, setHideUpload] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFilesBackup, setSelectedFilesBackup] = useState([]);
  const [allowedTypes, setAllowedTypes] = useState([
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/csv",
  ]);

  const [errorList, setErrorList] = useState([]);
  const [focusedFile, setFocusedfile] = useState(null);
  const [callTimeSelectionError, setCallTimeSelectionError] = useState(false);
  const [selectRandom, setSelectRandom] = useState(true);
  const [isDates, setIsDates] = useState(true);
  const navigate = useNavigate();
  const [frequency, setFrequency] = useState({});
  const format = "MM-DD-YYYY";
  const [continueClicked, setContinueClicked] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [selectedRowIdx, setSelectedRowIdx] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  // NEW: support selecting multiple rows via their stable internal IDs
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [targetPage, setTargetPage] = useState(null);
  const [isSelectingRow, setIsSelectingRow] = useState(false);

  const [errorOpen, setErrorOpen] = useState(false);

  const [openOverlay, setOpenOverlay] = useState(false);
  const [currentBatchId, setCurrentBatchId] = useState("");
  const [insufficientCall, setInsufficientCall] = useState(false);

  const dropAreaRef = useRef(null);

  const navigateHome = useHome();

  const [loading, setLoading] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(null); // { jobId, status, message }
  const [ehrNoDataMessage, setEhrNoDataMessage] = useState(null); // Message when EHR returns no data
  const [isFirstSelectOpen, setIsFirstSelectOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState("");
  const [addPatients, setAddPatients] = useState(false);

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [confirmationValue, setConfirmationValue] = useState(null);
  const [filename, setFileName] = useState(null);

  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isRestrictedPopupVisible, setIsRestrictedPopupVisible] =
    useState(false);

  // Billing restriction states
  const [showAccessRestrictionPopup, setShowAccessRestrictionPopup] =
    useState(false);
  const [showUnitsWarningPopup, setShowUnitsWarningPopup] = useState(false);
  const [showExpiredUnitsPopup, setShowExpiredUnitsPopup] = useState(false);
  const [accessRestrictionMessage, setAccessRestrictionMessage] = useState("");
  const [unitsWarningMessage, setUnitsWarningMessage] = useState("");
  const [expiredUnitsMessage, setExpiredUnitsMessage] = useState("");

  // Trial plan states
  const [isTrialUser, setIsTrialUser] = useState(false);
  const [showTrialWelcomePopup, setShowTrialWelcomePopup] = useState(false);
  const [showTrialDataLimitPopup, setShowTrialDataLimitPopup] = useState(false);
  const [trialDataLimitMessage, setTrialDataLimitMessage] = useState("");

  // Trial batch limit states
  const [showTrialBatchLimitPopup, setShowTrialBatchLimitPopup] =
    useState(false);
  const [trialBatchLimitMessage, setTrialBatchLimitMessage] = useState("");

  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedUsecase, setSelectedUsecase] = useState("");
  const [selectedSubUsecase, setSelectedSubUsecase] = useState("");
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [showUsecaseSelection, setShowUsecaseSelection] = useState(false);
  const [showQuestionSelection, setShowQuestionSelection] = useState(false);
  
  // Question Set states
  const [questionSets, setQuestionSets] = useState([]);
  const [selectedQuestionSet, setSelectedQuestionSet] = useState(null);
  const [loadingQuestionSets, setLoadingQuestionSets] = useState(false);
  const [questionSetDropdownOpen, setQuestionSetDropdownOpen] = useState(false);

  // State to track missing selections for visual feedback
  const [missingSelections, setMissingSelections] = useState({
    branch: false,
    usecase: false,
    subUsecase: false,
  });
  
  const [batchAliasError, setBatchAliasError] = useState(false);

  const [files, setFiles] = useState([]);
  const [tableData, setTableData] = useState([]);

  const [uploadMessage, setUploadMessage] = useState("");

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedSlotUtcStartTime, setSelectedSlotUtcStartTime] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [useCases, setUseCases] = useState([]);
  const [loadingUseCases, setLoadingUseCases] = useState(true);
  const [branchOptions, setBranchOptions] = useState([]);
  const [validEhrs, setValidEhrs] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [availableEhrsFromFacilities, setAvailableEhrsFromFacilities] = useState([]);
  const [selectedEhrFacilityId, setSelectedEhrFacilityId] = useState("");

  const [errorCellCount, setErrorCellCount] = useState(0);
  const [isManualEntry, setIsManualEntry] = useState(false);

  const [scheduleResult, setScheduleResult] = useState({
    open: false,
    success: false,
    message: "",
  });

  const [totalErrorCount, setTotalErrorCount] = useState(0);
  const [correctedErrorsCount, setCorrectedErrorsCount] = useState(0);
  const [lastValidatedTableData, setLastValidatedTableData] = useState([]);

  const [needsValidation, setNeedsValidation] = useState(true);
  const [hasManualEdits, setHasManualEdits] = useState(false);

  const [showValidationMessage, setShowValidationMessage] = useState(false);

  const [rawPatientIds, setRawPatientIds] = useState(
    ehrDynamicFields['patient_ids'] ? ehrDynamicFields['patient_ids'].join(', ') : ''
  );

  const dummyColumns = [
    "Member EHR id",
    "payorName",
    "Payor Phone number to dial",
    "Appointment Date",
    "Member First Name",
    "Member Middle Name",
    "Member Last Name",
    "Member DOB (MM/DD/YYYY)",
    "Member SSN",
    "Insurance Plan Type (Medical/Dental)",
    "providerFirstName",
    "Primary Insurance Name",
    "Primary Member ID",
    "Primary Group Member ID",
    "Member Callback number on Insurance card",
    "Member Street",
    "Member State",
    "Member City",
    "Member Zipcode",
    "Member's Phone number",
    "Secondary First Name",
    "Secondary Middle Name",
    "Secondary Last Name",
    "Secondary Insurance Name",
    "Secondary Member ID",
    "Secondary Group Member ID",
    "Member relationship to Secondary",
    "InPatient/OutPatient",
    "Place Type (Clinic/Facility)",
    "CPT code",
    "Diagnosis / ICD -10",
    "Comments",
  ];

  function removeDuplicates(arr) {
    return arr.filter(
      (item, index, self) =>
        index ===
        self.findIndex((obj) => JSON.stringify(obj) === JSON.stringify(item))
    );
  }

  // Function to ensure all rows have unique IDs
  const ensureRowIds = (data) => {
    // Safety check: ensure data is an array
    if (!Array.isArray(data)) {
      console.error("ensureRowIds called with non-array data:", data);
      return [];
    }
    
    return data.map((row, index) => ({
      ...row,
      _rowId: row._rowId || `row_${Date.now()}_${index}_${Math.random()}`
    }));
  };

  const sendNotificationForInsufficientCalls = async () => {
    await checkRemainingCalls({
      notify: true,
      justCheck: false,
    });
    setInsufficientCall(false);
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false);
    setIsRestrictedPopupVisible(false);
    window.location.href = "/home";
  };

  const fetcbAccountDetails = async () => {
    const response = await getAccountDetails();
    const { accountDetails } = response;

    if (accountDetails?.unitsRemaining < 0) {
      setIsPopupVisible(true);
    }
    if (
      !(
        accountDetails?.currentJob?.status === "Ready" ||
        accountDetails?.currentJob?.status === "Completed"
      )
    ) {
      setIsRestrictedPopupVisible(true);
    }
  };

  const handleConfirmationClose = () => setIsConfirmationOpen(false);
  const handleConfirmationOpen = () => setIsConfirmationOpen(true);

  const handleConfirmationValueChange = (e) => {
    handleConfirmationOpen();
    setConfirmationValue(e);
    setFileName(e.target.files[0]?.name);
  };

  const handleConfirmed = () => {
    handleFileInput(confirmationValue);
    handleConfirmationClose();
    setConfirmationValue(null);
  };

  const handleFirstSelectToggle = () => {
    setIsFirstSelectOpen(!isFirstSelectOpen);
  };

  useEffect(() => {
    setSelectedDate(null);
  }, [selectRandom]);

  const generateBatchId = (sessionId) => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `${sessionId}:${month}${day}${year}${hours}${minutes}${seconds}`.replace(
      /%3a/g,
      ":"
    );
  };

  const handleRangeToggle = (value) => {
    setSelectRandom(value);
  };

  const submitValidate = () => {
    console.log("submitValidate check:", {
      selectedBranch,
      selectedUsecase,
      selectedSubUsecase,
      selectedQuestions: selectedQuestions.length,
      selectedDate,
      selectedTime,
      batchAlias: batchAlias.trim()
    });
    return selectedBranch && selectedUsecase && selectedSubUsecase && selectedDate && selectedTime && batchAlias.trim();
  };

  const dateConversion = (date) => {
    if (!date) return null;
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${mm}-${dd}-${year}`;
  };

  const handleLoadPreviousFile = (load) => {
    load ? setSelectedFiles(selectedFilesBackup) : setSelectedFilesBackup([]);
  };

  function formatCurrentDateToMMDDYYYY() {
    const now = new Date();
    const utcDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    const month = String(utcDate.getUTCMonth() + 1).padStart(2, "0");
    const day = String(utcDate.getUTCDate()).padStart(2, "0");
    const year = utcDate.getUTCFullYear();
    return `${month}-${day}-${year}`;
  }

  const handleContinue = () => {
    // Validate batch alias
    if (!batchAlias.trim()) {
      setBatchAliasError(true);
      message.error("Please enter a batch alias name");
      return;
    }
    setBatchAliasError(false);
    
    // For API mode, fetch data directly and render table
    if (selectedType === "API") {
      if (!selectedBranch || !selectedUsecase || !selectedSubUsecase) {
        message.error("Please select branch, usecase, and subusecase before continuing");
        return;
      }
      
      // Validate EHR configuration
      if (!selectedEhr) {
        message.error("Please configure EHR settings");
        setShowEhrConfigPopup(true);
        return;
      }

      // Get the configuration for this EHR and usecase
      const ehrUsecaseConfig = ehrConfig?.[selectedEhr]?.[selectedUsecase];
      if (!ehrUsecaseConfig || !ehrUsecaseConfig.supported) {
        message.error(`${ALL_EHRS.find((e) => e.key === selectedEhr)?.displayName || selectedEhr} is not configured for ${selectedUsecase.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}. Please select a different EHR system or use case.`);
        return;
      }

      // Validate all required fields
      const requiredFields = ehrUsecaseConfig.required_fields || [];
      const missingFields = [];

      for (const field of requiredFields) {
        let value = null;
        
        if (field === 'start_date') value = ehrStartDate;
        else if (field === 'end_date') value = ehrEndDate;
        else value = ehrDynamicFields[field];
        
        if (!value) {
          missingFields.push(field);
        }
      }

      if (missingFields.length > 0) {
        const fieldNames = missingFields.map(f => f.replace(/_/g, ' ')).join(', ');
        message.error(`Please provide required field(s): ${fieldNames}`);
        setShowEhrConfigPopup(true);
        return;
      }

      // Validate facility selection when EHR is configured in multiple facilities
      const facilitiesWithSelectedEhr = facilities.filter((f) => {
        const mappings = f.ehrMappings || {};
        return Object.keys(mappings).some((k) => {
          const normalized = ALL_EHRS.find((e) => e.key.toLowerCase() === k.toLowerCase())?.key || k;
          return normalized.toLowerCase() === selectedEhr.toLowerCase();
        });
      });
      if (facilitiesWithSelectedEhr.length > 0 && !selectedEhrFacilityId) {
        message.error("Please select a facility");
        setShowEhrConfigPopup(true);
        return;
      }
      
      setLoading(true);
      
      // Format dates to MM/DD/YYYY for API
      const formatMmDdYyyy = (date) => {
        const d = new Date(date);
        if (isNaN(d.getTime())) return null;
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const y = d.getFullYear();
        return `${m}/${day}/${y}`;
      };

      const fetchParams = {
        branch: selectedBranch,
        usecase: selectedUsecase,
        sub_usecase: selectedSubUsecase,
        ehr_name: selectedEhr,
      };

      if (selectedEhrFacilityId) fetchParams.facility_id = selectedEhrFacilityId;

      // Add date fields if present (mm/dd/yyyy format)
      if (ehrStartDate) fetchParams.start_date = formatMmDdYyyy(ehrStartDate);
      if (ehrEndDate) fetchParams.end_date = formatMmDdYyyy(ehrEndDate);

      const normalizeDynamicFieldValue = (input) => {
        console.log("normalizeDynamicFieldValue input:", input);
        if (input === undefined || input === null) return "";
        if (Array.isArray(input)) return input;
        if (typeof input === "object") {
          // Support multiple dropdown/event payload shapes safely.
          const extracted =
            input.value ??
            input.id ??
            input.target?.value ??
            input.currentTarget?.value ??
            input.option?.value ??
            input.data?.value ??
            input.selectedOption?.value ??
            "";
          return extracted;
        }
        return input;
      };

      // Add all dynamic fields (normalize select/event objects to primitive values)
      Object.keys(ehrDynamicFields).forEach((field) => {
        const rawValue = ehrDynamicFields[field];
        const normalizedValue = normalizeDynamicFieldValue(rawValue);

        if (
          normalizedValue !== undefined &&
          normalizedValue !== null &&
          `${normalizedValue}`.trim() !== ""
        ) {
          fetchParams[field] = normalizedValue;
        }
      });
      // Defensive include: keep appointment_type_id even when value may be numeric-like
      if (
        ehrDynamicFields?.appointment_type_id !== undefined &&
        ehrDynamicFields?.appointment_type_id !== null &&
        `${ehrDynamicFields.appointment_type_id}`.trim() !== ""
      ) {
        fetchParams.appointment_type_id = `${ehrDynamicFields.appointment_type_id}`;
      }
      // Normalize plural/snake-case variants to the expected API key
      if (
        ehrDynamicFields?.appointment_type_ids !== undefined &&
        ehrDynamicFields?.appointment_type_ids !== null &&
        `${ehrDynamicFields.appointment_type_ids}`.trim() !== ""
      ) {
        fetchParams.appointment_type_id = `${ehrDynamicFields.appointment_type_ids}`;
      }
      // Defensive include: keep department_id even when value may be numeric-like
      if (
        ehrDynamicFields?.department_id !== undefined &&
        ehrDynamicFields?.department_id !== null &&
        `${ehrDynamicFields.department_id}`.trim() !== ""
      ) {
        fetchParams.department_id = `${ehrDynamicFields.department_id}`;
      }
      const departmentState = ehrDynamicFields?.state;
      fetchParams.state =
        departmentState !== undefined &&
        departmentState !== null &&
        `${departmentState}`.trim() !== ""
          ? `${departmentState}`.trim()
          : null;
      // Defensive include: keep document_type_id from varied dropdown payload shapes
      const normalizedDocumentTypeId = normalizeDynamicFieldValue(
        ehrDynamicFields?.document_type_id
      );
      if (
        normalizedDocumentTypeId !== undefined &&
        normalizedDocumentTypeId !== null &&
        `${normalizedDocumentTypeId}`.trim() !== ""
      ) {
        fetchParams.document_type_id = `${normalizedDocumentTypeId}`;
      }
      
      // Add polling callback to update UI
      fetchParams.onPollingUpdate = (pollingInfo) => {
        console.log('Polling update:', pollingInfo);
        setPollingStatus(pollingInfo);
      };
      
      fetchEhrPatientsData(fetchParams)
        .then((res) => {
          if (res && res.statusCode === "SUCCESS") {
            // Updated structure: response now contains dataList and total_count
            const data = res.response?.dataList || [];
            const totalCount = res.response?.total_count || 0;
            console.log(`✅ Fetched ${data.length} patients (total: ${totalCount}) from EHR`);
            
            // Check if no data was returned
            if (data.length === 0) {
              // Set the no data message to display in UI
              setEhrNoDataMessage(res.message || "No data found for the specified criteria");
              setContinueClicked(true);
              message.info(res.message || "No data found");
              return;
            }
            
            // Debug: Check if EHR fields are present in the response
            if (data.length > 0) {
              const sampleRow = data[0];
              const ehrFields = Object.keys(sampleRow).filter(key => key.startsWith("EHR"));
              console.log("🔍 EHR fields in response:", ehrFields);
              console.log("🔍 Sample row from EHR:", sampleRow);
            }
            
            const dataWithIds = ensureRowIds(data);
            setTableDataWithIds(dataWithIds);
            setFilteredTableData(dataWithIds);
            setShowValidationMessage(false); // Only show after user clicks Validate
            setContinueClicked(true);
            setEhrNoDataMessage(null); // Clear any previous no data message
            message.success(res.message || `Successfully fetched ${totalCount} patient(s)`);
          } else if (res && res.statusCode === "NO_DATA") {
            // Handle NO_DATA response
            setEhrNoDataMessage(res?.error || res?.message || "No appointments found for the specified date range");
            setContinueClicked(true);
            message.warning(res?.error || "No appointments found for the specified date range");
          } else {
            message.error(res?.message || res?.error || "Failed to fetch patients data");
          }
        })
        .catch((err) => {
          console.error("API mode fetch error:", err);
          // Handle error response from API (like 404)
          const errorMessage = err?.response?.data?.error || 
                               err?.response?.data?.message || 
                               err?.message || 
                               "Failed to fetch patients data";
          
          // Show warning for NO_DATA status, error for other failures
          if (err?.response?.data?.statusCode === "NO_DATA") {
            setEhrNoDataMessage(errorMessage);
            setContinueClicked(true);
            message.warning(errorMessage);
          } else {
            message.error(errorMessage);
          }
        })
        .finally(() => {
          setLoading(false);
          setPollingStatus(null); // Stop polling
        });
      return;
    }
    // Manual mode: proceed as before
    handleSubmit();
    setContinueClicked(true);
  };

  // Reset table and continue area when path selections change to avoid stale requests/data
  useEffect(() => {
    setContinueClicked(false);
    setTableDataWithIds([]);
    setFilteredTableData([]);
    setShowValidationMessage(false); // Only show after user clicks Validate
    setEhrNoDataMessage(null); // Clear no data message
  }, [selectedBranch, selectedUsecase, selectedSubUsecase]);

  const handleSubmit = async () => {
    const batchId =
      currentBatchId || generateBatchId(sessionStorage.getItem("sessionId"));
    setCurrentBatchId(batchId);

    const currentUTCDate = formatCurrentDateToMMDDYYYY();
    const formattedTime = new Date().toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

    const data = {
      payor: "",
      batch_request_id: batchId,
      recurring: isReccuring,
      selected_date: selectedDate ? formatDate(selectedDate) : null,
      time: selectedTime,
      excludable_days: ["Saturday", "Sunday"],
      excludable_dates: [],
      uploaded_by: sessionStorage.getItem("mail"),
      uploaded_date: currentUTCDate,
      uploaded_time: convertToUTC(formattedTime),
      executed_by: "",
    };

    setFrequency(data);
  };

  const formatDate = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const handleCancel = () => {
    navigate("/home");
  };

  const resetData = () => {
    setSelectedTime("");
    setSelectedSlotUtcStartTime("");
    setSelectedDate(null);
    setSearchText("");
    setIsReccuring(false);
    setContinueClicked(false);
    setSelectedFiles([]);
    setSelectedFilesBackup([]);
    setSelectedBranch("");
    setSelectedUsecase("");
    setSelectedSubUsecase("");
    setSelectedQuestions([]);
    setIsManualEntry(false);
    setTableData([]);
    setFilteredTableData([]);
    setShowTrialWelcomePopup(false);
    setShowTrialDataLimitPopup(false);
    setTrialDataLimitMessage("");
    setShowTrialBatchLimitPopup(false);
    setTrialBatchLimitMessage("");
    setTotalErrorCount(0);
    setCorrectedErrorsCount(0);
    setLastValidatedTableData([]);
    setNeedsValidation(true);
    setHasManualEdits(false);
    // Clear batch alias state
    setBatchAlias("");
    setBatchAliasError(false);
    setAliasValidation({
      isChecking: false,
      isValid: null,
      message: ""
    });
    // Clear animation states
    setIsAnimatingAlias(false);
    setAnimatedAliasText("");
    setShowAnimation(false);
    // Clear missing selections visual feedback
    setMissingSelections({
      branch: false,
      usecase: false,
      subUsecase: false,
    });
    // Clear question set selection
    setQuestionSets([]);
    setSelectedQuestionSet(null);
    // Clear EHR configuration
    setSelectedType('manual');
    setSelectedEhr('');
    setEhrPopupStep(1);
    setEhrStartDate(null);
    setEhrEndDate(null);
    setEhrDynamicFields({});
    setEhrDropdownData({});
    setPollingStatus(null);
    setEhrNoDataMessage(null);
  };

  const handleFileInput = (e) => {
    setHideUpload(false);
    const newFiles = Array.from(e.target.files).filter(
      (file) =>
        allowedTypes.includes(file.type) &&
        !selectedFiles.some((selectedFile) => selectedFile.name === file.name)
    );

    // Set the files state - FileUploader will handle the actual upload
    setSelectedFiles([...selectedFiles, ...newFiles]);
    setAllowedTypes((prevAllowedTypes) => [
      ...prevAllowedTypes,
      ...newFiles.map((file) => file.type),
    ]);
    e.target.value = null;

    // If FileUploader sets tableData, check for errors and set needsValidation
    // This requires a useEffect on tableData
  };

  const handleSearch = (value) => {
    setSearchText(value);
    if (value.trim() === "") {
      setFilteredTableData(tableData);
    } else {
      const filteredData = tableData.filter((row) => {
        const searchText = value.toLowerCase();
        return Object.keys(row).some((key) => {
          if (typeof row[key] === "string") {
            return row[key].toLowerCase().includes(searchText);
          }
          return false;
        });
      });
      // Ensure filtered data has row IDs
      const filteredWithIds = ensureRowIds(filteredData);
      setFilteredTableData(filteredWithIds);
    }
  };

  // Only sync filteredTableData with tableData when not searching
  useEffect(() => {
    if (searchText.trim() === "" && tableData.length > 0) {
      console.log("=== SYNC DEBUG ===");
      console.log("Syncing filteredTableData with tableData");
      console.log("tableData length:", tableData.length);
      console.log("tableData sample:", tableData[0]);
      console.log("Current selectedRowIdx:", selectedRowIdx);
      console.log("Current selectedRowId:", selectedRowId);
      // Ensure row IDs are preserved when syncing
      const dataWithIds = ensureRowIds(tableData);
      console.log("dataWithIds sample:", dataWithIds[0]);
      setFilteredTableData(dataWithIds);
    }
  }, [tableData, searchText]);

  // Restore selection when filteredTableData changes (but not during active row selection)
  useEffect(() => {
    // Skip restoration if user is actively selecting a row
    if (isSelectingRow) {
      console.log("=== SKIPPING RESTORATION - User is actively selecting ===");
      return;
    }
    
    console.log("=== SELECTION RESTORATION DEBUG ===");
    console.log("selectedRowId:", selectedRowId);
    console.log("filteredTableData.length:", filteredTableData.length);
    console.log("current selectedRowIdx:", selectedRowIdx);
    
    if (selectedRowId && filteredTableData.length > 0) {
      const newIndex = filteredTableData.findIndex(row => row._rowId === selectedRowId);
      console.log("Found row at index:", newIndex);
      if (newIndex !== -1 && newIndex !== selectedRowIdx) {
        console.log("Restoring selection to index:", newIndex, "for rowId:", selectedRowId);
        setSelectedRowIdx(newIndex);
      }
    }
  }, [filteredTableData, selectedRowId, selectedRowIdx, isSelectingRow]);

  // Wrapper function to ensure row IDs are added when setting table data
  const setTableDataWithIds = (data) => {
    console.log("setTableDataWithIds called with:", typeof data, data);
    
    if (typeof data === 'function') {
      // Handle function callbacks (like in setState)
      setTableData((prevData) => {
        const newData = data(prevData);
        console.log("Function callback returned:", newData);
        return ensureRowIds(newData);
      });
    } else {
      // Handle direct array data
      const dataWithIds = ensureRowIds(data);
      setTableData(dataWithIds);
    }
  };
  
  // Row selection handler - now supports multi-select using stable row IDs
  const handleRowSelect = (index, rowIdFromTable) => {
    console.log("=== ROW SELECTION DEBUG ===");
    console.log("Row selected (absolute index):", index);
    console.log("Row ID from table:", rowIdFromTable);
    console.log("filteredTableData length:", filteredTableData.length);
    console.log("tableData length:", tableData.length);

    // Set flag to prevent restoration effect from interfering
    setIsSelectingRow(true);

    if (index < 0 || index >= filteredTableData.length) {
      console.log("Invalid index - clearing selection");
      setSelectedRowId(null);
      setSelectedRowIdx(null);
      setSelectedRowIds([]);
      setIsSelectingRow(false);
      return;
    }

    const selectedRow = filteredTableData[index];
    const effectiveRowId = rowIdFromTable || selectedRow?._rowId;

    console.log("Selected row data:", selectedRow);
    console.log("Effective row _rowId:", effectiveRowId);

    if (!effectiveRowId) {
      console.log("Selected row does not have a valid _rowId, skipping selection");
      setIsSelectingRow(false);
      return;
    }

    setSelectedRowIds((prev) => {
      const alreadySelected = prev.includes(effectiveRowId);
      const next = alreadySelected
        ? prev.filter((id) => id !== effectiveRowId)
        : [...prev, effectiveRowId];

      console.log("Updated selectedRowIds:", next);

      if (next.length === 0) {
        setSelectedRowId(null);
        setSelectedRowIdx(null);
      } else {
        // When selecting: use this row as primary. When deselecting: keep another selected row as primary
        const newPrimaryId = alreadySelected
          ? (next.includes(selectedRowId) ? selectedRowId : next[next.length - 1])
          : effectiveRowId;
        const newPrimaryIdx = filteredTableData.findIndex((r) => r?._rowId === newPrimaryId);
        setSelectedRowId(newPrimaryId);
        setSelectedRowIdx(newPrimaryIdx >= 0 ? newPrimaryIdx : index);
      }

      return next;
    });

    // Reset the flag after state updates have settled
    setTimeout(() => {
      setIsSelectingRow(false);
    }, 100);
  };

  const handleSelectAll = (selectAll) => {
    if (selectAll) {
      const allIds = filteredTableData
        .map((row) => row?._rowId)
        .filter(Boolean);
      setSelectedRowIds(allIds);
      if (allIds.length > 0) {
        setSelectedRowId(allIds[allIds.length - 1]);
        setSelectedRowIdx(filteredTableData.length - 1);
      }
    } else {
      setSelectedRowIds([]);
      setSelectedRowId(null);
      setSelectedRowIdx(null);
    }
  };

  const handleTargetPageReached = () => {
    console.log("Target page reached, clearing targetPage");
    setTargetPage(null); // Reset to null (no navigation)
  };
  const tableContainerRef = useRef(null);

  // Temporarily disabled click outside handler to debug selection issue
  // useEffect(() => {
  //   function handleClickOutside(event) {
  //     // Check if the click is on an Add or Delete button
  //     const isAddButton = event.target.closest('[data-action="add"]');
  //     const isDeleteButton = event.target.closest('[data-action="delete"]');
      
  //     if (tableContainerRef.current && !tableContainerRef.current.contains(event.target) && !isAddButton && !isDeleteButton) {
  //       console.log("=== CLICK OUTSIDE DEBUG ===");
  //       console.log("Clearing selection due to click outside");
  //       console.log("Target element:", event.target);
  //       setSelectedRowIdx(null);
  //       setSelectedRowId(null);
  //     }
  //   }
  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //   };
  // }, []);

  const handleAdd = async () => {
    console.log("=== ADD FUNCTION DEBUG ===");
    
    // Check if trial user can add more patients
    if (isTrialUser) {
      const canAdd = await canTrialUserAddPatient();
      if (!canAdd) {
        console.log("CallAutomation - Trial user cannot add more patients, showing restriction popup");
        setTrialBatchLimitMessage(
          `You are on a trial plan limited to ${TRIAL_USER_PATIENT_LIMIT} total calls across all active batches. You have reached your limit. Please wait for existing batches to complete or contact your administrator to upgrade your plan.`
        );
        setShowTrialBatchLimitPopup(true);
        return;
      }
    }
    
    console.log("Add called with selectedRowIdx:", selectedRowIdx);
    console.log("Add called with selectedRowId:", selectedRowId);
    console.log("filteredTableData length:", filteredTableData.length);
    console.log("tableData length:", tableData.length);
    console.log("First filtered row has _rowId:", filteredTableData[0]?._rowId ? "YES" : "NO");
    console.log("First table row has _rowId:", tableData[0]?._rowId ? "YES" : "NO");
    if (selectedRowId) {
      const selectedRow = filteredTableData.find(row => row._rowId === selectedRowId);
      console.log("Selected row data:", selectedRow);
      console.log("Selected row _rowId:", selectedRow?._rowId);
    }
    
    // Get column structure from current data or use dummy columns
    let columnStructure = {};
    if (filteredTableData.length > 0) {
      columnStructure = Object.keys(filteredTableData[0]).reduce((obj, col) => ({ ...obj, [col]: "" }), {});
    } else if (tableData.length > 0) {
      columnStructure = Object.keys(tableData[0]).reduce((obj, col) => ({ ...obj, [col]: "" }), {});
    } else {
      columnStructure = dummyColumns.reduce((obj, col) => ({ ...obj, [col]: "" }), {});
    }
    
    // Add unique row ID
    const newRow = { ...columnStructure, _rowId: Date.now() + Math.random() };
    console.log("New row created:", newRow);
    
    // Update tableData first, let useEffect handle filteredTableData sync
    let newTableData = [];
    let newRowIndex = -1;
    
    if (selectedRowId) {
      console.log("Inserting after selected row with ID:", selectedRowId);
      
      // Find the corresponding index in the main tableData
      const mainIndex = tableData.findIndex(row => row._rowId === selectedRowId);
      
      if (mainIndex !== -1) {
        newRowIndex = mainIndex + 1;
        newTableData = [
          ...tableData.slice(0, mainIndex + 1),
          newRow,
          ...tableData.slice(mainIndex + 1),
        ];
      } else {
        newRowIndex = tableData.length;
        newTableData = [...tableData, newRow];
      }
    } else {
      console.log("Adding to end - no row selected");
      // Add to end
      newRowIndex = tableData.length;
      newTableData = [...tableData, newRow];
    }
    
    console.log("New table data length:", newTableData.length);
    console.log("New row inserted at index:", newRowIndex);
    
    // Update tableData first, let useEffect handle filteredTableData sync
    setTableDataWithIds(newTableData);
    setHasManualEdits(true);
    setNeedsValidation(true);
    
    // Navigate to the page where the new row is located
    setTimeout(() => {
      const rowsPerPage = 10; // Default from table component
      const targetPage = Math.floor(newRowIndex / rowsPerPage) + 1;
      console.log("=== PAGE NAVIGATION DEBUG ===");
      console.log("New row inserted at index:", newRowIndex);
      console.log("Rows per page:", rowsPerPage);
      console.log("Calculated target page:", targetPage);
      console.log("Total pages needed:", Math.ceil(newTableData.length / rowsPerPage));
      
      setTargetPage(targetPage);
    }, 100);
    
    // Don't clear selection immediately - let user see the new row
  };

  const handleUpload = async () => {
    const batchId = currentBatchId;
    setBatchId(batchId);
    setHideUpload(true);
    setLoading(true);

    const branch = selectedBranch;
    const usecase = selectedUsecase;
    const subUsecase = selectedSubUsecase;

    const providerTimeZone = getProviderTimezone();

    try {
      await pushProviderPatientData(
        {
          frequency,
          data: filteredTableData,
          provider_timezone: providerTimeZone,
        },
        sessionStorage.getItem("mail"),
        sessionStorage.getItem("sessionId"),
        batchId,
        branch,
        usecase,
        subUsecase
      )
        .then((res) => {
          sessionStorage.setItem("insufficient_storage_error", "{}");
          if (res && res.responseData && res.responseData.message) {
            setUploadMessage(res.responseData.message);
          } else if (res && res.message) {
            setUploadMessage(res.message);
          }
          setErrorOpen(true);
          if (res && res.csv_data) {
            setErrorList(res);
                    const responseDataWithIds = ensureRowIds(res.response);
        setTableDataWithIds(responseDataWithIds);
        setFilteredTableData(responseDataWithIds);
          }
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          const errorResponse = error.response;
          if (
            errorResponse.status === 500 &&
            errorResponse.data.errorMessage === "Insufficient Capacity"
          ) {
            sessionStorage.setItem(
              "insufficient_storage_error",
              JSON.stringify(errorResponse.data.response)
            );
            setErrorOpen(true);
            setContinueClicked(false);
          }
          console.error("Error Data:", error);
        });
    } catch (error) {
      console.error("Error in handleUpload:", error);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setErrorOpen(false);
    navigate("/home");
  };

  const handleFileDrop = (droppedFiles) => {
    handleFileInput({ target: { files: droppedFiles } });
  };

  const options = [
    { value: "manual", label: "Manual" },
    { value: "api", label: "API" },
  ];

  const handleBack = () => {
    setSelectedFilesBackup(selectedFiles);
    setSelectedFiles([]);
    setAddPatients(false);
    setTableData([]);
    setFilteredTableData([]);
    setContinueClicked(false);
    setIsManualEntry(false);
    setTotalErrorCount(0);
    setCorrectedErrorsCount(0);
    setLastValidatedTableData([]);
    setNeedsValidation(true);
    setHasManualEdits(false);
    setSelectedRowIdx(null);
    setSelectedRowId(null);
    setSelectedRowIds([]);
  };

  const handleDelete = () => {
    console.log("=== DELETE FUNCTION DEBUG ===");
    console.log("Delete called with selectedRowIdx:", selectedRowIdx);
    console.log("Delete called with selectedRowId:", selectedRowId);
    console.log("Delete called with selectedRowIds:", selectedRowIds);
    console.log("filteredTableData length:", filteredTableData.length);
    console.log("tableData length:", tableData.length);
    console.log(
      "First filtered row has _rowId:",
      filteredTableData[0]?._rowId ? "YES" : "NO"
    );
    console.log(
      "First table row has _rowId:",
      tableData[0]?._rowId ? "YES" : "NO"
    );

    if (!selectedRowIds || selectedRowIds.length === 0) {
      console.log("Delete conditions not met - no rows selected");
      return;
    }

    const idsToDelete = new Set(selectedRowIds);
    console.log("Row IDs to delete:", Array.from(idsToDelete));

    // Remove selected rows from main table data using row IDs
    const newTableData = tableData.filter((row) => !idsToDelete.has(row._rowId));

    console.log("New table data length after delete:", newTableData.length);

    // Update tableData first, let useEffect handle filteredTableData sync
    setTableDataWithIds(newTableData);
    setSelectedRowIdx(null);
    setSelectedRowId(null);
    setSelectedRowIds([]);
    setHasManualEdits(true);
    setNeedsValidation(true);
  };

  const handleUsecaseSelect = (usecase, subUsecase) => {
    const selectedUseCase = useCases.find(
      (uc) =>
        uc.branch === selectedBranch &&
        uc.usecase === usecase &&
        uc.subUsecase === subUsecase
    );

    if (selectedUseCase) {
      setSelectedUsecase(usecase);
      setSelectedSubUsecase(subUsecase);
      setShowUsecaseSelection(false);
      setSelectedQuestions([]);
      // Clear missing selection state when usecase and subUsecase are selected
      setMissingSelections(prev => ({
        ...prev,
        usecase: false,
        subUsecase: false
      }));
    }
  };

  const handleQuestionSelect = (questions) => {
    setSelectedQuestions(questions);
    setShowQuestionSelection(false);
  };

  const handleTimeSlotSelect = (slotId) => {
    setSelectedTime(slotId);
    // Find the selected slot and store its utcStartTime
    const selectedSlot = availableSlots.find((slot) => slot.slotId === slotId);
    if (selectedSlot) {
      setSelectedSlotUtcStartTime(selectedSlot.utcStartTime);
      console.log("Selected slot:", selectedSlot);
      console.log("Stored utcStartTime:", selectedSlot.utcStartTime);
    } else {
      console.warn("Selected slot not found in availableSlots");
    }
  };

  const formatUsecaseName = (name) => {
    if (!name) return "";
    return name
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleError = (errorMessage) => {
    console.error(errorMessage);
  };

  const handleFileSubmit = async (data) => {
    console.log("File submit data:", data);
  };

  const isMemberSsnColumn = (key) =>
    typeof key === "string" &&
    key.replace(/\s+/g, "").toLowerCase() === "memberssn";

  function getColumnsFromData(data) {
    if (!data || data.length === 0) return [];
    const columnOrder = Object.keys(data[0]);
    
    // Define static columns to hide from table display
    const staticHiddenColumns = [
      "facilityId",
      "departmentId",
      "_rowId"
    ];
    
    // Filter out hidden columns: static ones + anything starting with "EHR"
    // Hide Member SSN when data is pulled from EHR/API (PHI not shown in API mode UI)
    let visibleColumns = columnOrder.filter((key) => 
      !staticHiddenColumns.includes(key) &&
      !key.startsWith("EHR") &&
      !(selectedType === "API" && isMemberSsnColumn(key))
    );
    
    // Reorder columns to ensure proper order: Group/Facility NPI → Department Name → Rendering Provider NPI
    const reorderColumns = (columns) => {
      const facilityNPIIndex = columns.findIndex(col => 
        (col.toLowerCase().includes('group') && col.toLowerCase().includes('npi')) ||
        (col.toLowerCase().includes('facility') && col.toLowerCase().includes('npi'))
      );
      const departmentNameIndex = columns.findIndex(col => 
        col.toLowerCase().includes('department') && col.toLowerCase().includes('name')
      );
      const renderingProviderNPIIndex = columns.findIndex(col => 
        col.toLowerCase().includes('rendering provider npi')
      );
      
      // Only reorder if we have the columns that need reordering
      const hasColumns = [facilityNPIIndex, departmentNameIndex, renderingProviderNPIIndex].filter(idx => idx !== -1);
      
      if (hasColumns.length >= 2) {
        const reordered = [...columns];
        const extractedColumns = [];
        
        // Extract columns in reverse order of their indices to maintain array integrity
        const indicesToExtract = [facilityNPIIndex, departmentNameIndex, renderingProviderNPIIndex]
          .filter(idx => idx !== -1)
          .sort((a, b) => b - a); // Sort in descending order
          
        // Extract columns and store with their original names
        const columnData = [];
        indicesToExtract.forEach(idx => {
          columnData.unshift({ name: reordered[idx], originalIndex: idx });
          reordered.splice(idx, 1);
        });
        
        // Insert columns in the desired order: Facility → Department → Rendering Provider
        const insertionPoint = Math.min(...indicesToExtract.filter(idx => idx !== -1));
        const orderedColumns = [];
        
        // Add facility column if it exists
        const facilityCol = columnData.find(col => {
          const name = col.name.toLowerCase();
          return (name.includes('group') && name.includes('npi')) ||
                 (name.includes('facility') && name.includes('npi'));
        });
        if (facilityCol) orderedColumns.push(facilityCol.name);
        
        // Add department column if it exists  
        const departmentCol = columnData.find(col => {
          const name = col.name.toLowerCase();
          return name.includes('department') && name.includes('name');
        });
        if (departmentCol) orderedColumns.push(departmentCol.name);
        
        // Add rendering provider column if it exists
        const renderingCol = columnData.find(col => {
          const name = col.name.toLowerCase();
          return name.includes('rendering provider npi');
        });
        if (renderingCol) orderedColumns.push(renderingCol.name);
        
        // Insert the ordered columns
        reordered.splice(insertionPoint, 0, ...orderedColumns);
        
        console.log('🔄 Reordered columns: Group/Facility NPI → Department Name → Rendering Provider NPI');
        return reordered;
      }
      
      return columns;
    };
    
    visibleColumns = reorderColumns(visibleColumns);
    
    return visibleColumns.map((key) => {
      // Increase width for NPI dropdown columns
      let columnWidth = `${100 / visibleColumns.length}%`;
      const keyLower = key.toLowerCase();
      
      if ((keyLower.includes('group') && keyLower.includes('npi')) || 
          (keyLower.includes('facility') && keyLower.includes('npi')) || 
          (keyLower.includes('department') && keyLower.includes('name')) ||
          keyLower.includes('rendering provider npi')) {
        columnWidth = '22rem'; // Fixed width for dropdown columns - responsive rem units
      }
      
      return {
        name: key,
        label: key,
        selector: (row) => row[key] || "",
        reorder: true,
        width: columnWidth,
        renderName: key,
        headerName: key,
        cell: (row) => {
          const value = row[key] || "";
          const isError =
            (typeof value === "string" && value.startsWith("Missing Value")) ||
            (typeof value === "string" && value.startsWith("Invalid"));
  return (
            <div
              style={{
                color: isError ? "#ff4e3a" : "inherit",
                fontWeight: isError ? "500" : "normal",
              }}
            >
              {value}
                                                                                                        </div>
          );
        },
      };
    });
  }

  const columns = getColumnsFromData(filteredTableData);
  
  // Ensure data has row IDs and log structure
  useEffect(() => {
    if (filteredTableData.length > 0) {
      console.log("Filtered table data sample:", filteredTableData[0]);
      console.log("Table data sample:", tableData[0]);
      console.log("Selected row index:", selectedRowIdx);
      
      // Check if data needs row IDs
      const filteredNeedsIds = filteredTableData.some(row => !row._rowId);
      const tableNeedsIds = tableData.some(row => !row._rowId);
      
      if (filteredNeedsIds || tableNeedsIds) {
        console.log("Adding row IDs to data...");
        if (filteredNeedsIds) {
          const filteredWithIds = ensureRowIds(filteredTableData);
          setFilteredTableData(filteredWithIds);
        }
        if (tableNeedsIds) {
          const tableWithIds = ensureRowIds(tableData);
          setTableData(tableWithIds);
        }
      }
    }
  }, [filteredTableData, tableData, selectedRowIdx]);

  // Update error count when filteredTableData changes
  useEffect(() => {
    if (filteredTableData.length > 0) {
      const newErrorCount = countErrorCells(filteredTableData, columns);
      setErrorCellCount(newErrorCount);
    } else {
      setErrorCellCount(0);
    }
  }, [filteredTableData, columns]);

  // --- LIVE ERROR COUNT (for corrections) ---
  useEffect(() => {
    if (
      lastValidatedTableData.length > 0 &&
      columns.length > 0 &&
      totalErrorCount > 0
    ) {
      const lastValidatedErrors = totalErrorCount;
      const currentErrors = countErrorCells(filteredTableData, columns);
      const corrected = Math.max(0, lastValidatedErrors - currentErrors);
      setCorrectedErrorsCount(corrected);
    } else {
      setCorrectedErrorsCount(0);
    }
  }, [filteredTableData, columns, lastValidatedTableData, totalErrorCount]);

  // --- INITIALIZE STATIC ERROR COUNT ON FIRST VALIDATION ---
  useEffect(() => {
    // If tableData is set for the first time (e.g. after upload), set static error count
    if (tableData.length > 0 && totalErrorCount === 0 && columns.length > 0) {
      const initialErrorCount = countErrorCells(tableData, columns);
      setTotalErrorCount(initialErrorCount);
      setLastValidatedTableData(tableData);
    }
  }, [tableData, columns]);

  // Set needsValidation based on whether there are errors after file upload
  useEffect(() => {
    if (tableData.length > 0 && !isManualEntry && columns.length > 0 && !hasManualEdits) {
      // Only check if data was added (not on initial empty state)
      // and not during manual entry (which already sets it)
      // and not if there have been manual edits
      const errorCount = countErrorCells(tableData, columns);
      if (errorCount === 0) {
        // No errors found, allow direct submission
        setNeedsValidation(false);
        setTotalErrorCount(0);
        setLastValidatedTableData(tableData);
        // Don't show "No errors" message - only show after user clicks Validate
      } else {
        // Errors found, require validation
        setNeedsValidation(true);
        setTotalErrorCount(errorCount);
        // Show error message immediately when data has errors
        setShowValidationMessage(true);
      }
    }
  }, [tableData.length, isManualEntry, columns, hasManualEdits]);

  const handleValidate = async () => {
    try {
      setLoading(true);
      const metadata = {
        branch: selectedBranch,
        usecase: selectedUsecase,
        subUsecase: selectedSubUsecase,
      };

      const patientFileData = [
        {
          filename: filename || "uploaded-data.xlsx",
          data: tableData, // always send full data
        },
      ];

      const response = await validatePatientFiles({
        metadata,
        patientFileData,
      });
      setShowValidationMessage(true);
      if (response.statusCode === "SUCCESS") {
        let validatedData = response.response;

        // Check trial restrictions after validation
        // WARNING: not going in!!
        if (isTrialUser) {
          try {
            // Get existing batches to check total limits
            const batchesResponse = await getBatches();
            console.log(batchesResponse, 'TRIAL DEBUG: batchesResponseDebugger')
            const batches = (batchesResponse?.batches && Array.isArray(batchesResponse.batches)) 
              ? batchesResponse.batches 
              : [];
            console.log(batches, 'TRIAL DEBUG: batchesDebugger')
            // Validate trial user limits with current validated data
            const validationResult = validateTrialUserLimits(batches, validatedData.length);
            console.log(`🔍 TRIAL DEBUG: Validation - Checking ${validatedData.length} calls against existing batches`);
            
            if (!validationResult.canProceed) {
              // Call limit exceeded, try to trim data to fit
              const availableCalls = TRIAL_USER_PATIENT_LIMIT - validationResult.totalActiveCalls;
              
              if (availableCalls > 0) {
                const originalCount = validatedData.length;
                validatedData = validatedData.slice(0, availableCalls);
                
                setTrialDataLimitMessage(
                  `You are on a trial plan limited to ${TRIAL_USER_PATIENT_LIMIT} total calls across all active batches. You currently have ${validationResult.totalActiveCalls} active calls. We have automatically limited your data to ${availableCalls} patients (removed ${originalCount - availableCalls} patients). If you need to process more patients, please upgrade your plan or upload a smaller file.`
                );
                setShowTrialDataLimitPopup(true);
              } else {
                // No available calls at all
                setTrialDataLimitMessage(validationResult.message);
                setShowTrialDataLimitPopup(true);
                setLoading(false);
                return;
              }
            }
          } catch (error) {
            console.error("CallAutomation - Error checking trial limits during validation:", error);
            // Fallback to old logic if batch check fails
            if (validatedData.length > TRIAL_USER_PATIENT_LIMIT) {
              const originalCount = validatedData.length;
              validatedData = validatedData.slice(0, TRIAL_USER_PATIENT_LIMIT);
              
              setTrialDataLimitMessage(
                `You are on a trial plan which limits you to ${TRIAL_USER_PATIENT_LIMIT} patients per batch. We have automatically limited your data to the first ${TRIAL_USER_PATIENT_LIMIT} patients (removed ${originalCount - TRIAL_USER_PATIENT_LIMIT} patients).`
              );
              setShowTrialDataLimitPopup(true);
            }
          }
        }

        // Preserve EHR fields and other metadata from original tableData and merge with validated data
        const validatedDataWithEhrFields = validatedData.map((validatedRow, index) => {
          const originalRow = tableData[index];
          if (!originalRow) return validatedRow;
          
          // Extract EHR fields and metadata fields (facilityId, departmentId, etc.) from original row
          const preservedFields = {};
          Object.keys(originalRow).forEach(key => {
            // Preserve EHR fields, facilityId, and departmentId
            if (key.startsWith("EHR") || key === "facilityId" || key === "departmentId") {
              preservedFields[key] = originalRow[key];
            }
          });
          
          // Merge validated data with preserved fields (validated data takes precedence for other fields)
          return { ...validatedRow, ...preservedFields };
        });
        
        console.log("🔍 Preserving EHR fields after validation");
        if (validatedDataWithEhrFields.length > 0) {
          const ehrFieldsInMerged = Object.keys(validatedDataWithEhrFields[0]).filter(k => k.startsWith("EHR"));
          console.log("✅ EHR fields after merge:", ehrFieldsInMerged);
        }
        
        const validatedDataWithIds = ensureRowIds(validatedDataWithEhrFields);
        setTableDataWithIds(validatedDataWithIds);
        setFilteredTableData(validatedDataWithIds);
        const newErrorCount = countErrorCells(validatedDataWithEhrFields, columns);
        setTotalErrorCount(newErrorCount);
        setCorrectedErrorsCount(0);
        setLastValidatedTableData(validatedDataWithIds);
        setNeedsValidation(false);
        setHasManualEdits(false);
      } else {
        console.error(
          "Validation failed:",
          response.message || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Validation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCellChange = (rowIndex, colName, value) => {
    // Identify row by index (from the cell being edited) so edits apply to the correct row
    // even when multiple rows are selected or the edited row isn't the primary
    const rowToEdit = filteredTableData[rowIndex];
    const targetRowId = rowToEdit?._rowId;
    if (!targetRowId) return;

    const updateRow = (rowToUpdate, updateValue) => {
      if (typeof updateValue === 'object' && updateValue !== null) {
        return { ...rowToUpdate, ...updateValue };
      }
      return { ...rowToUpdate, [colName]: updateValue };
    };

    const updateTableData = (prevData, updateLogic) => {
      const editedRow = prevData.find(row => row._rowId === targetRowId);
      if (editedRow) {
        const updatedRow = updateLogic(editedRow, value);
        return prevData.map(row => row._rowId === targetRowId ? updatedRow : row);
      }
      return prevData;
    };

    setTableData(prev => updateTableData(prev, updateRow));

    if (searchText) {
      setFilteredTableData(prev => updateTableData(prev, updateRow));
    }
    
    // Mark that edits have been made
    setHasManualEdits(true);
    setNeedsValidation(true); 
    setShowValidationMessage(false); // Only show after user clicks Validate
  };

  const addNewPatient = (newPatientData = {}) => {
    console.log("Adding new patient, current filtered data length:", filteredTableData.length);
    // ... existing code ...
  };

  // Fetch slots when date changes
  useEffect(() => {
    const fetchSlots = async () => {
      if (!selectedDate) {
        setAvailableSlots([]);
        setSelectedTime("");
        setSelectedSlotUtcStartTime("");
        setLoadingSlots(false);
        return;
      }
      setLoadingSlots(true);
      const providerTimezone = getProviderTimezone();
      const mm = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const dd = String(selectedDate.getDate()).padStart(2, "0");
      const yyyy = selectedDate.getFullYear();
      const localDate = `${mm}/${dd}/${yyyy}`;
      try {
        const res = await getSlotAvailability({ providerTimezone, localDate });
        if (
          res.statusCode === "SUCCESS" &&
          res.response &&
          Array.isArray(res.response.slots)
        ) {
          setAvailableSlots(
            res.response.slots.filter((slot) => slot.isAvailable)
          );
        } else {
          setAvailableSlots([]);
        }
        setSelectedTime("");
        setSelectedSlotUtcStartTime("");
      } catch (e) {
        setAvailableSlots([]);
        setSelectedTime("");
        setSelectedSlotUtcStartTime("");
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
  }, [selectedDate]);

  // Add useEffect to fetch customer details and use cases
  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        setLoadingUseCases(true);
        setLoading(true); // Show loader during fetch
        const customerDetailsResponse = await getCustomerDetails();
        const customerDetails = customerDetailsResponse.response;
        if (customerDetails && customerDetails.useCasesRef) {
          // Transform useCasesRef into branch options format
          const uniqueBranches = [
            ...new Set(customerDetails.useCasesRef.map((uc) => uc.branch)),
          ];
          const formattedBranchOptions = uniqueBranches.map((branch) => ({
            value: branch,
            label: formatUsecaseName(branch),
          }));
          setUseCases(customerDetails.useCasesRef);
          setBranchOptions(formattedBranchOptions);
          
          // Extract valid_ehrs
          if (customerDetails.valid_ehrs && Array.isArray(customerDetails.valid_ehrs)) {
            setValidEhrs(customerDetails.valid_ehrs);
          } else {
            setValidEhrs([]);
          }
        }
      } catch (error) {
        console.error("Error fetching customer details:", error);
        // Set default branch options if fetch fails
        setBranchOptions([]);
        setValidEhrs([]);
      } finally {
        setLoadingUseCases(false);
        setLoading(false);
      }
    };

    fetchCustomerDetails();
  }, []);

  // Fetch facilities and derive available EHRs (EHRs configured in at least one facility)
  useEffect(() => {
    const fetchFacilitiesAndEhrs = async () => {
      try {
        const data = await getFacilities();
        const list = Array.isArray(data) ? data : data?.response || [];
        setFacilities(list);
        const ehrKeys = new Set();
        list.forEach((f) => {
          const mappings = f.ehrMappings || {};
          Object.keys(mappings).forEach((rawKey) => {
            const found = ALL_EHRS.find((e) => e.key.toLowerCase() === String(rawKey).toLowerCase());
            ehrKeys.add(found ? found.key : rawKey);
          });
        });
        setAvailableEhrsFromFacilities(Array.from(ehrKeys));
      } catch (error) {
        console.error("Error fetching facilities:", error);
        setFacilities([]);
        setAvailableEhrsFromFacilities([]);
      }
    };
    fetchFacilitiesAndEhrs();
  }, []);

  // Merge validEhrs (from customer details) with availableEhrsFromFacilities - EHR is available if in either
  const availableEhrs = [...new Set([
    ...validEhrs.map((v) => ALL_EHRS.find((e) => e.key.toLowerCase() === v.toLowerCase())?.key || v),
    ...availableEhrsFromFacilities,
  ].filter(Boolean))];

  // Fetch EHR endpoint configuration on component mount
  useEffect(() => {
    const fetchEhrConfig = async () => {
      try {
        setLoadingEhrConfig(true);
        const config = await getEhrEndpointConfig();
        setEhrConfig(config);
        console.log('✅ EHR Configuration loaded:', config);
      } catch (error) {
        console.error('Error fetching EHR configuration:', error);
        message.error('Failed to load EHR configuration');
      } finally {
        setLoadingEhrConfig(false);
      }
    };

    fetchEhrConfig();
  }, []);

  // Fetch question sets when branch, usecase, and subusecase are selected
  useEffect(() => {
    const fetchQuestionSets = async () => {
      if (!selectedBranch || !selectedUsecase || !selectedSubUsecase) {
        setQuestionSets([]);
        setSelectedQuestionSet(null);
        return;
      }

      try {
        setLoadingQuestionSets(true);
        const response = await getCustomerQuestionUploads({
          branch: selectedBranch,
          usecase: selectedUsecase,
          subusecase: selectedSubUsecase,
          status: 'completed'
        });

        if (response.statusCode === 'SUCCESS' && response.response?.uploads) {
          setQuestionSets(response.response.uploads);
        } else {
          setQuestionSets([]);
        }
      } catch (error) {
        console.error('Error fetching question sets:', error);
        setQuestionSets([]);
      } finally {
        setLoadingQuestionSets(false);
      }
    };

    fetchQuestionSets();
  }, [selectedBranch, selectedUsecase, selectedSubUsecase]);

  // Fetch dropdown data dynamically based on EHR config when EHR changes
  useEffect(() => {
    const fetchEhrDropdowns = async () => {
      if (!selectedEhr || !ehrConfig || !selectedUsecase || !selectedEhrFacilityId) {
        // Clear dropdown data if no EHR selected
        setEhrDropdownData({});
        setEhrDynamicFields({});
        console.log('returned because no selectedEhrFacilityId', selectedEhrFacilityId)
        return;
      }

      // Get the configuration for this EHR and usecase
      const ehrUsecaseConfig = ehrConfig[selectedEhr]?.[selectedUsecase];
      if (!ehrUsecaseConfig || !ehrUsecaseConfig.supported) {
        console.log(`${ALL_EHRS.find((e) => e.key === selectedEhr)?.displayName || selectedEhr} is not configured for ${selectedUsecase.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`);
        return;
      }

      // Combine required and optional fields
      const allFields = [
        ...(ehrUsecaseConfig.required_fields || []),
        ...(ehrUsecaseConfig.optional_fields || [])
      ];

      // Identify fields that need dropdown data
      const dropdownFields = allFields.filter(field =>
        [
          'office_id',
          'location_id',
          'provider_id',
          'referring_provider_id',
          'document_type_id',
          // NEW: Athena-specific dynamic dropdowns
          'appointment_type_id',
          'department_id',
        ].includes(field)
      );

      if (dropdownFields.length === 0) {
        return;
      }

      // Fetch dropdown data for each field
      const dropdownPromises = dropdownFields.map(async (field) => {
        try {
          setLoadingEhrDropdowns(prev => ({ ...prev, [field]: true }));
          
          // Map field name to query type for EHR data
          let queryType = '';
          if (field === 'office_id') queryType = 'offices';
          else if (field === 'location_id') queryType = 'locations';
          else if (field === 'provider_id') queryType = 'providers';
          else if (field === 'referring_provider_id') queryType = 'referring_providers';
          else if (field === 'document_type_id') queryType = 'document_types';
          else if (field === 'appointment_type_id') queryType = 'appointment_types';
          else if (field === 'department_id') queryType = 'departments';
          
          const response = await queryEhrData(queryType, selectedEhr, selectedEhrFacilityId);
          
          if (response.statusCode === 'SUCCESS' && response.response) {
            // Auto-select if only one option
            if (response.response.length === 1) {
              const firstItem = response.response[0];
              let value = '';
              
              if (field === 'office_id') value = firstItem.office_id;
              else if (field === 'location_id') value = firstItem.id;
              else if (field === 'provider_id') value = firstItem.provider_id;
              else if (field === 'referring_provider_id') value = firstItem.provider_id;
              else if (field === 'document_type_id') value = firstItem.documenttypeid || firstItem.document_type_id || firstItem.id;
              else if (field === 'appointment_type_id') value = firstItem.appointment_type_id || firstItem.id;
              else if (field === 'department_id') value = firstItem.department_id || firstItem.id;
              
              if (field === 'department_id') {
                setEhrDynamicFields(prev => ({
                  ...prev,
                  [field]: value,
                  state: firstItem?.address?.state ?? null,
                }));
              } else {
                setEhrDynamicFields(prev => ({ ...prev, [field]: value }));
              }
            }
            
            return { field, data: response.response };
          }
          return { field, data: [] };
        } catch (error) {
          const fieldLabel = field.replace(/_/g, ' ');
          console.error(`Error fetching ${fieldLabel} data:`, error);
          message.error(`Failed to fetch ${fieldLabel}`);
          return { field, data: [] };
        } finally {
          setLoadingEhrDropdowns(prev => ({ ...prev, [field]: false }));
        }
      });

      const results = await Promise.all(dropdownPromises);
      
      // Update dropdown data state
      const newDropdownData = {};
      results.forEach(result => {
        newDropdownData[result.field] = result.data;
      });
      
      setEhrDropdownData(newDropdownData);
    };

    fetchEhrDropdowns();
  }, [selectedEhr, selectedUsecase, ehrConfig, selectedEhrFacilityId]);

  const handleSchedule = async () => {
    console.log("Submit button clicked, handleSchedule called");
    console.log("CallAutomation - Current isTrialUser state:", isTrialUser);
    console.log("Selected slot utcStartTime:", selectedSlotUtcStartTime);
    try {
      setLoading(true);

      // Check trial batch limits if user is on trial plan
      if (isTrialUser) {
        console.log("CallAutomation - Checking trial batch limits");
        const batchesResponse = await getBatches();
        console.log("CallAutomation - Batches response:", batchesResponse);

        // Extract batches from API response structure
        const batches = (batchesResponse?.batches && Array.isArray(batchesResponse.batches)) 
          ? batchesResponse.batches 
          : [];
        const currentBatchCallCount = (filteredTableData || tableData || []).length;
        
        console.log(`🔍 TRIAL DEBUG: Scheduling - Found ${batches.length} batches, trying to schedule ${currentBatchCallCount} calls`);
        
        // Validate trial user limits
        const validationResult = validateTrialUserLimits(batches, currentBatchCallCount);
        console.log("CallAutomation - Trial validation result:", validationResult);
        
        if (!validationResult.canProceed) {
          setTrialBatchLimitMessage(validationResult.message);
          setShowTrialBatchLimitPopup(true);
          setLoading(false);
          return;
        }
        
        console.log("CallAutomation - Trial user validation passed, allowing scheduling");
      }

      // Use the stored utcStartTime from the selected slot
      const startDateTime =
        selectedSlotUtcStartTime || new Date().toISOString();
      console.log("Using startDateTime:", startDateTime);
      
      // Determine source: "api" if API mode, otherwise "manual" or "file"
      let source = "file"; // default
      if (selectedType === "API") {
        source = "api";
      } else if (isManualEntry) {
        source = "manual";
      }
      
      const metadata = {
        startDateTime: startDateTime,
        branch: selectedBranch,
        usecase: selectedUsecase,
        subUsecase: selectedSubUsecase,
        uploadedAt: new Date().toISOString(),
        providerTimezone: getProviderTimezone(),
        customQuestionIds: selectedQuestions.map((q) => q.id),
        source: source,
        questionSetId: selectedQuestionSet?._id || null,
        ...(batchAlias.trim() && { alias: batchAlias.trim() }),
        // Add ehrName if API mode
        ...(selectedType === "API" && selectedEhr && { ehrName: selectedEhr }),
      };
      console.log("Scheduling with metadata:", metadata);
      
      // Verify EHR fields are preserved in data before sending to backend
      if (tableData.length > 0) {
        const sampleRow = tableData[0];
        const ehrFields = Object.keys(sampleRow).filter(key => key.startsWith("EHR"));
        console.log("🔍 All keys in sample row:", Object.keys(sampleRow));
        if (ehrFields.length > 0) {
          console.log("✅ EHR fields preserved in data:", ehrFields);
          console.log("✅ Sample EHR values:", ehrFields.reduce((obj, key) => ({ ...obj, [key]: sampleRow[key] }), {}));
        } else {
          console.warn("⚠️ No EHR fields found in tableData!");
        }
      }
      
      const response = await scheduleBatch({
        metadata,
        data: tableData, // always send full data with EHR columns
      });
      console.log("Schedule API response:", response);
      if (response.statusCode === "SUCCESS") {
        message.success("Batch Created Successfully");
        setTimeout(() => navigate("/home"), 1200);
      } else {
        message.error(response.message || "Failed to schedule batch.");
        setTimeout(() => navigate("/home"), 1800);
        message.error(response.message || "Failed to schedule batch.");
        setTimeout(() => navigate("/home"), 1800);
      }
    } catch (error) {
      console.error("Schedule error:", error);
      message.error("An error occurred while scheduling.");
      setTimeout(() => navigate("/home"), 1800);
      message.error("An error occurred while scheduling.");
      setTimeout(() => navigate("/home"), 1800);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseScheduleResult = () => {
    setScheduleResult({ open: false, success: false, message: "" });
    navigate("/home");
  };

  const handleAddManually = async () => {
    try {
      setLoading(true);
      setIsManualEntry(true);
      setNeedsValidation(true);
      setShowValidationMessage(false); // Don't show validation message until actual validation

      // Construct path for API call
      const path = `${selectedBranch}-${selectedUsecase}-${selectedSubUsecase}`;

      // Get column headers from API
      const response = await getColumnHeaders(path);

      if (response.statusCode === "SUCCESS" && response.response) {
        const headers = response.response;

        // Create initial empty rows with the headers
        const emptyRow = {};
        headers.forEach((header) => {
          emptyRow[header] = "";
        });

        // Create a few empty rows to start with
        const initialData = [emptyRow];

        console.log("Initial data: ManualDataDebugger", initialData);
        console.log("Empty row ManualDataDebugger", emptyRow);

        const initialDataWithIds = ensureRowIds(initialData);
        setTableDataWithIds(initialDataWithIds);
        setFilteredTableData(initialDataWithIds);
        setFileName("manual-entry.xlsx");
      } else {
        console.error(
          "Failed to get column headers:",
          response.message || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Error getting column headers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    // Check if all required selections are made
    const missing = [];
    const missingState = {
      branch: false,
      usecase: false,
      subUsecase: false,
    };

    if (!selectedBranch) {
      missing.push("Branch");
      missingState.branch = true;
    }
    if (!selectedUsecase) {
      missing.push("Usecase");
      missingState.usecase = true;
    }
    if (!selectedSubUsecase) {
      missing.push("Sub-usecase");
      missingState.subUsecase = true;
    }

    if (missing.length > 0) {
      setMissingSelections(missingState);
      const missingText = missing.join(", ");
      const action = missing.length === 1 ? "selection" : "selections";
      message.error(`Please complete the following ${action}: ${missingText}`);
      return;
    }

    // Clear missing selections if all are selected
    setMissingSelections({
      branch: false,
      usecase: false,
      subUsecase: false,
    });

    try {
      setLoading(true);
      
      // Construct path for API call
      const path = `${selectedBranch}-${selectedUsecase}-${selectedSubUsecase}`;

      // Get column headers from API
      const response = await getColumnHeaders(path);

      if (response.statusCode === "SUCCESS" && response.response) {
        const headers = response.response;

        // Create a workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([headers]);

        // Add the worksheet to the workbook
        XLSX.utils.book_append_sheet(wb, ws, "Template");

        // Generate filename
        const filename = `${selectedBranch}-${selectedUsecase}-${selectedSubUsecase}-template.xlsx`;

        // Write the file and trigger download
        XLSX.writeFile(wb, filename);

        message.success(`Excel template downloaded successfully as "${filename}"`);
      } else {
        message.error("Failed to get column headers: " + (response.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error downloading template:", error);
      message.error("Error downloading template. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Check page access on component mount
  useEffect(() => {
    const checkAccess = async () => {
      const accessCheck = await checkPageAccess();
      if (accessCheck.isExpiredUnits) {
        // Check if user already acknowledged the expired units warning this session
        const hasAcknowledgedExpiredUnits = sessionStorage.getItem('acknowledgedExpiredUnits');
        if (!hasAcknowledgedExpiredUnits) {
          setExpiredUnitsMessage(accessCheck.message);
          setShowExpiredUnitsPopup(true);
        }
      } else if (!accessCheck.canProceed) {
        setAccessRestrictionMessage(accessCheck.message);
        setShowAccessRestrictionPopup(true);
      } else {
        setAccessRestrictionMessage(accessCheck.message);
      }
    };
    checkAccess();
  }, []);

  // Check units availability when filteredTableData changes
  useEffect(() => {
    const checkUnits = async () => {
      if (filteredTableData.length > 0) {
        const unitsCheck = await checkUnitsAvailability(
          filteredTableData.length
        );
        if (unitsCheck.isExpiredUnits) {
          // Already showing expired units popup from checkPageAccess, don't show another
          return;
        }
        if (!unitsCheck.canProceed) {
          setUnitsWarningMessage(unitsCheck.message);
          setShowUnitsWarningPopup(true);
        }
      }
    };
    checkUnits();
  }, []);

  // Check trial status on component mount
  useEffect(() => {
    const checkTrial = async () => {
      const trialStatus = await checkTrialStatus();
      console.log("CallAutomation - Trial status check:", trialStatus);
      setIsTrialUser(trialStatus.isTrialUser);
      console.log(
        "CallAutomation - Setting isTrialUser to:",
        trialStatus.isTrialUser
      );

      // Check subscription status first
      if (
        !trialStatus.hasActiveSubscription ||
        trialStatus.subscriptionStatus !== "active"
      ) {
        console.log(
          "CallAutomation - No active subscription, showing subscription message"
        );
        let message = "";

        if (trialStatus.subscriptionStatus === "cancelled") {
          message =
            "Your subscription has been cancelled. Please ask your administrator to reactivate your calling plan to access calling features.";
        } else if (trialStatus.subscriptionStatus === "past_due") {
          message =
            "Your subscription payment is past due. Please ask your administrator to update the payment information to restore access to calling features.";
        } else {
          message =
            "You do not have an active calling plan. Please ask your administrator to set up a calling plan to access calling features.";
        }

        setTrialBatchLimitMessage(message);
        setShowTrialBatchLimitPopup(true);
        return;
      }

      // Show appropriate popup for trial users with active subscription
      if (trialStatus.isTrialUser) {
        console.log(
          "CallAutomation - Trial user detected, checking for existing batches"
        );

        // Check for existing batches
        try {
          const batchesResponse = await getBatches();
          console.log(batchesResponse, 'TRIAL DEBUG: batchesResponseDebugger')
          const batches = (batchesResponse?.batches && Array.isArray(batchesResponse.batches)) 
            ? batchesResponse.batches 
            : [];
          
          console.log(
            "CallAutomation - Initial batches check:",
            batches
          );

          // Check current call usage
          const validationResult = validateTrialUserLimits(batches, 0);
          console.log("CallAutomation - Trial limits check:", validationResult);

          const activeBatchCount = validationResult.activeBatchCount;
          const totalActiveCalls = validationResult.totalActiveCalls;
          const availableCalls = TRIAL_USER_PATIENT_LIMIT - totalActiveCalls;
          
          console.log(
            `CallAutomation - Trial user status: ${totalActiveCalls}/${TRIAL_USER_PATIENT_LIMIT} calls used across ${activeBatchCount} batches`
          );

          // If user has reached their call limit, show restriction popup and redirect home
          if (totalActiveCalls >= TRIAL_USER_PATIENT_LIMIT) {
            console.log("CallAutomation - Trial user has reached call limit, showing restriction popup");
            setTrialBatchLimitMessage(
              `You are on a trial plan limited to ${TRIAL_USER_PATIENT_LIMIT} total calls across all active batches. You currently have ${totalActiveCalls} active calls and have reached your limit. Please wait for existing batches to complete or contact your administrator to upgrade your plan.`
            );
            setShowTrialBatchLimitPopup(true);
          } else {
            // Show welcome popup with current status info
            setShowTrialWelcomePopup(true);
          }
        } catch (error) {
          console.error("CallAutomation - Error checking batches:", error);
          // Fallback to welcome popup if batch check fails
          setShowTrialWelcomePopup(true);
        }
      }
    };
    checkTrial();
  }, []);

  // Function to handle trial data limit callback from FileUploader
  const handleTrialDataLimit = async (originalCount, suggestedLimitCount, uploadedData) => {
    // This will be called when FileUploader detects trial user with large upload
    // But we'll do the real restriction logic here where getBatches() works
    if (isTrialUser) {
      try {
        console.log(`🔍 TRIAL DEBUG: handleTrialDataLimit called with original: ${originalCount}, suggested: ${suggestedLimitCount}, uploadedData length: ${uploadedData?.length || 'undefined'}`);
        
        // Get current batches where we know it works
        const batchesResponse = await getBatches();
        console.log('🔍 TRIAL DEBUG: Raw batchesResponse in handleTrialDataLimit:', batchesResponse);
        console.log('🔍 TRIAL DEBUG: batchesResponse?.response:', batchesResponse?.response);
        console.log('🔍 TRIAL DEBUG: batchesResponse?.batches:', batchesResponse?.batches);
        console.log('🔍 TRIAL DEBUG: Is batches array?', Array.isArray(batchesResponse?.batches));
        
        const batches = (batchesResponse?.batches && Array.isArray(batchesResponse.batches)) 
          ? batchesResponse.batches 
          : [];
        
        console.log('🔍 TRIAL DEBUG: Final extracted batches array:', batches);
        console.log(`🔍 TRIAL DEBUG: Found ${batches.length} total batches in handleTrialDataLimit`);
        console.log('🔍 TRIAL DEBUG: Batch details:', batches.map(b => ({ id: b.id, status: b.status, totalCalls: b.totalCalls })));
        
        // Validate trial user limits with current uploaded data
        const validationResult = validateTrialUserLimits(batches, originalCount);
        console.log('🔍 TRIAL DEBUG: Validation result in handleTrialDataLimit:', {
          canProceed: validationResult.canProceed,
          totalActiveCalls: validationResult.totalActiveCalls,
          totalCallsAfterNewBatch: validationResult.totalCallsAfterNewBatch,
          callLimitExceeded: validationResult.callLimitExceeded
        });
        
        if (!validationResult.canProceed) {
          const availableCalls = TRIAL_USER_PATIENT_LIMIT - validationResult.totalActiveCalls;
          
          if (availableCalls > 0) {
            // Trim the uploaded data to fit available calls  
            const currentTableData = uploadedData || tableData || [];
            const trimmedData = currentTableData.slice(0, availableCalls);
            
            console.log(`🔍 TRIAL DEBUG: RESTRICTION APPLIED! Original: ${originalCount}, Available calls: ${availableCalls}, Trimmed to: ${trimmedData.length}`);
            
            // Update the table with trimmed data
            const trimmedDataWithIds = trimmedData.map((row, index) => ({
              ...row,
              _rowId: row._rowId || `row_${Date.now()}_${index}_${Math.random()}`
            }));
            
            setTableDataWithIds(trimmedDataWithIds);
            setFilteredTableData(trimmedDataWithIds);
            
            // Show appropriate message
            const removedCount = originalCount - availableCalls;
            setTrialDataLimitMessage(
              `You are on a trial plan limited to ${TRIAL_USER_PATIENT_LIMIT} total calls across all active batches. You currently have ${validationResult.totalActiveCalls} active calls. We have automatically limited your data to ${availableCalls} patients (removed ${removedCount} patients). If you need to process more patients, please upgrade your plan or upload a smaller file.`
            );
            setShowTrialDataLimitPopup(true);
          } else {
            // No available calls at all
            console.log(`🔍 TRIAL DEBUG: No available calls - clearing all data`);
            setTableDataWithIds([]);
            setFilteredTableData([]);
            
            setTrialDataLimitMessage(validationResult.message);
            setShowTrialDataLimitPopup(true);
          }
        } else {
          console.log(`🔍 TRIAL DEBUG: Validation PASSED - keeping all ${originalCount} calls`);
        }
        
      } catch (error) {
        console.error('🔍 TRIAL DEBUG: Error in handleTrialDataLimit:', error);
        // Fallback to simple per-batch limit
        const removedCount = Math.max(0, originalCount - TRIAL_USER_PATIENT_LIMIT);
        setTrialDataLimitMessage(
          `You are on a trial plan which limits you to ${TRIAL_USER_PATIENT_LIMIT} patients per batch. We have automatically limited your data to the first ${TRIAL_USER_PATIENT_LIMIT} patients (removed ${removedCount} patients). If you need to process more patients, please ask your administrator to upgrade your plan or upload a smaller file.`
        );
        setShowTrialDataLimitPopup(true);
      }
    }
  };

  // Function to check if trial user can add more patients
  const canTrialUserAddPatient = async () => {
    if (!isTrialUser) return true; // Non-trial users can always add
    
    try {
      // Get current batches and count active calls
      const batchesResponse = await getBatches();
      const batches = (batchesResponse?.batches && Array.isArray(batchesResponse.batches)) 
        ? batchesResponse.batches 
        : [];
      
      const totalActiveCalls = getTotalActiveCalls(batches);
      const currentBatchCalls = (filteredTableData || tableData || []).length;
      const totalCallsAfterAdd = totalActiveCalls + currentBatchCalls + 1; // +1 for the new patient
      
      console.log(`🔍 TRIAL DEBUG: canTrialUserAddPatient - Active calls: ${totalActiveCalls}, Current batch: ${currentBatchCalls}, After add: ${totalCallsAfterAdd}`);
      
      return totalCallsAfterAdd <= TRIAL_USER_PATIENT_LIMIT;
    } catch (error) {
      console.error('Error checking if trial user can add patient:', error);
      return true; // Allow on error to avoid blocking
    }
  };

  // Utility function to get abbreviation from a string
  function getAbbreviation(str) {
    if (!str) return "";
    return str
      .split(/\s+|_/)
      .filter(Boolean)
      .map((word) => word[0].toUpperCase())
      .join("");
  }

  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);
  const [showErrorsOnTop, setShowErrorsOnTop] = useState(false);
  const [errorsOnTopClicked, setErrorsOnTopClicked] = useState(false);

  // Batch alias states
  const [batchAlias, setBatchAlias] = useState("");
  const [aliasValidation, setAliasValidation] = useState({
    isChecking: false,
    isValid: null,
    message: ""
  });
  const [isGeneratingAlias, setIsGeneratingAlias] = useState(false);
  const [checkAliasTimeout, setCheckAliasTimeout] = useState(null);
  
  // Animation states for DecryptedText
  const [isAnimatingAlias, setIsAnimatingAlias] = useState(false);
  const [animatedAliasText, setAnimatedAliasText] = useState("");
  const [showAnimation, setShowAnimation] = useState(false);

  function rowHasError(row, columns) {
    return columns.some(col => {
      const value = row[col.name];
      return (typeof value === "string" && value.startsWith("Missing Value")) || (typeof value === "string" && value.startsWith("Invalid"));
    });
  }

  const handleShowErrorsOnTop = () => {
    const errorRows = filteredTableData.filter(row => rowHasError(row, columns));
    const nonErrorRows = filteredTableData.filter(row => !rowHasError(row, columns));
    setFilteredTableData([...errorRows, ...nonErrorRows]);
    setErrorsOnTopClicked(true);
  };

  // Batch alias functionality
  const handleGenerateBatchAlias = async () => {
    if (!selectedBranch || !selectedUsecase || !selectedSubUsecase) {
      message.error("Please select branch, usecase, and sub-usecase first");
      return;
    }

    setIsGeneratingAlias(true);
    try {
      const response = await generateBatchAlias({
        branch: selectedBranch,
        usecase: selectedUsecase,
        subusecase: selectedSubUsecase
      });
      
      if (response.statusCode === 'SUCCESS') {
        // Start the decrypted text animation
        setAnimatedAliasText(response.response.suggestedAlias);
        setShowAnimation(true);
        setIsAnimatingAlias(true);
        
        // Clear the input field to show animation
        setBatchAlias("");
      }
    } catch (error) {
      console.error("Error generating batch alias:", error);
      message.error("Failed to generate batch alias");
    } finally {
      setIsGeneratingAlias(false);
    }
  };

  // Handle animation completion
  const handleAnimationComplete = () => {
    setIsAnimatingAlias(false);
    setBatchAlias(animatedAliasText);
    setShowAnimation(false);
    setAliasValidation({
      isChecking: false,
      isValid: true,
      message: ""
    });
    message.success("Batch alias generated successfully!");
  };

  const checkAliasUniqueness = async (alias) => {
    if (!alias.trim()) {
      setAliasValidation({
        isChecking: false,
        isValid: null,
        message: ""
      });
      return;
    }

    setAliasValidation({
      isChecking: true,
      isValid: null,
      message: "Checking availability..."
    });

    try {
      const response = await checkBatchAliasUnique(alias.trim());
      
      if (response.statusCode === 'SUCCESS') {
        // Handle both possible response structures
        const responseData = response.data || response.response;
        const isUnique = responseData.is_unique;
        setAliasValidation({
          isChecking: false,
          isValid: isUnique,
          message: responseData.message || (isUnique ? "Alias is available" : "Alias already exists")
        });
      }
    } catch (error) {
      console.error("Error checking alias uniqueness:", error);
      setAliasValidation({
        isChecking: false,
        isValid: false,
        message: "Error checking availability"
      });
    }
  };

  const handleBatchAliasChange = (e) => {
    const value = e.target.value;
    setBatchAlias(value);
    // Clear error when user starts typing
    if (value.trim()) {
      setBatchAliasError(false);
    }
    
    // Debounce the uniqueness check
    if (checkAliasTimeout) {
      clearTimeout(checkAliasTimeout);
    }
    
    const timeout = setTimeout(() => {
      checkAliasUniqueness(value);
    }, 500);
    
    setCheckAliasTimeout(timeout);
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (checkAliasTimeout) {
        clearTimeout(checkAliasTimeout);
      }
    };
  }, [checkAliasTimeout]);

  return (
    <DataValidationOverlay
      batchId={batchId}
      open={errorOpen}
      closeOverlay={() => {
        setErrorOpen(false);
        navigateHome();
        setAddPatients(false);
      }}
      success={errorList.length === 0}
      failed={errorList.length !== 0}
      loading={loading}
      errorList={errorList}
      csvData={filteredTableData}
      setErrorOpen={setErrorOpen}
      message={uploadMessage}
    >
      {insufficientCall && (
        <CustomDialog
          open={insufficientCall}
          message={`We have noticed that the remaining call limit is insufficient. Would you like us to notify the responsible person to address this matter promptly?`}
          onConfirm={sendNotificationForInsufficientCalls}
          onCancel={() => setInsufficientCall(false)}
          themeMode={themeMode}
          currentTheme={currentTheme}
        />
      )}
      {(isPopupVisible || isRestrictedPopupVisible) && (
        <Popup
          style={{
            display: "flex",
            flexDirection: "column",
            maxWidth: "500px",
            width: "90%",
            background: currentTheme.loginPage.forceLoginBgColor,
            color: currentTheme.homePage.textColor,
            height: "10vh",
            overflowY: "auto",
            borderRadius: "16px",
            padding: "20px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
          visible={isPopupVisible || isRestrictedPopupVisible}
          onClose={handleClosePopup}
        >
          <button
            className="close-btn"
            onClick={() => handleClosePopup()}
            style={{ color: currentTheme.settingPage.textColor }}
          >
            {themeMode === "dark" ? (
              <img src="/icons/cancel-grey.svg" width={"20vh"} />
            ) : (
              <img src="/icons/cancel-orange.svg" width={"20vh"} />
            )}
          </button>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {isRestrictedPopupVisible
              ? "Restricted Access due to current batch in progress"
              : "Restricted due to Payment is Falied"}
                                                                                                      </div>
        </Popup>
      )}
      <div
        className="call-automation-root"
        style={{
          background: currentTheme.callAutomationPage.mainBackground,
          color: currentTheme.homePage.textColor,
          paddingRight: "2rem",
          overflow: "hidden",
          height: "100vh",
        }}
      >
        {/* Helper Button - Fixed in header area */}
        <HelperButton
          currentStep={0}
          onNextStep={() => {}}
          themeMode={themeMode}
          currentTheme={currentTheme}
          isVisible={true}
          selectedBranch={selectedBranch}
          selectedUsecase={selectedUsecase}
          selectedSubUsecase={selectedSubUsecase}
          selectedQuestions={selectedQuestions}
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          tableData={tableData}
          totalErrorCount={totalErrorCount}
          correctedErrorsCount={correctedErrorsCount}
          continueClicked={continueClicked}
          batchAlias={batchAlias}
          onDownloadTemplate={handleDownloadTemplate}
          onAddManually={handleAddManually}
          onValidate={handleValidate}
          onShowErrors={handleShowErrorsOnTop}
        />
        {tableData.length > 0 ? (
          <div
            style={{
              width: "100%",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                background: currentTheme.callAutomationPage.mainBackground,
                padding: "20px",
                borderRadius: "8px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '0 0 16px 0' }}>
                <Search
                  value={searchText}
                  onChange={handleSearch}
                  themeMode={themeMode}
                  currentTheme={currentTheme}
                  style={{ color: currentTheme.homePage.textColor, width: "100%" }}
                />
                {filteredTableData.some(row => rowHasError(row, columns)) && (
                  <Button
                    label="Show Errors on Top"
                    backgroundColor={currentTheme.callAutomationPage.activeButtonColor}
                    color={currentTheme.callAutomationPage.submitColor}
                    width="180px"
                    borderRadius="5px"
                    height="4vh"
                    onClick={handleShowErrorsOnTop}
                    style={{ marginLeft: 8 }}
                    disabled={errorsOnTopClicked}
                  />
                )}
                                                                                                    </div>
              {showValidationMessage && (
                <div
                  style={{
                    marginBottom: "1rem",
                    color: totalErrorCount > 0 ? "#ff4e3a" : "#4caf50",
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    textAlign: "left",
                  }}
                >
                  {totalErrorCount > 0
                    ? `Total Error Cells: ${totalErrorCount}`
                    : "No errors found. Ready to submit!"}
                  {lastValidatedTableData.length > 0 && totalErrorCount > 0 && (
                    <span
                      style={{
                        color: "#4caf50",
                        marginLeft: 16,
                        fontWeight: 500,
                      }}
                    >
                      {correctedErrorsCount > 0
                        ? `Corrected Errors: ${correctedErrorsCount}`
                        : ""}
                    </span>
                  )}
                                                                                                  </div>
              )}
              <CallAutomationInfo
                isRecursive={isReccuring}
                isMultipleDaysSelected={!selectRandom}
                selectedDate={selectedDate}
                handleValidate={handleValidate}
                handleSubmit={handleSubmit}
                selectedTime={selectedTime}
                endTime={selectedTime}
                handleBack={handleBack}
                handleAdd={handleAdd}
                handleDelete={handleDelete}
                isTrialUser={isTrialUser}
                canAddPatient={canTrialUserAddPatient}
                filteredTableData={filteredTableData}
                loading={loading}
                selectedQuestions={selectedQuestions}
                onShowQuestions={() => setShowQuestionSelection(true)}
                errorCellCount={totalErrorCount}
                onSchedule={handleSchedule}
                showSubmit={totalErrorCount === 0 && !needsValidation}
                needsValidation={needsValidation}
                selectedRowIdx={selectedRowIdx}
                selectedRowCount={selectedRowIds.length}
                selectedSlotDisplayTime={
                  availableSlots.find((slot) => slot.slotId === selectedTime)
                    ?.displayTime || undefined
                }
              />
                                                                                                </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                background: currentTheme.callAutomationPage.mainBackground,
                padding: "20px",
                borderRadius: "8px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "200px",
                  }}
                >
                  <LogoLoader />
                                                                                              </div>
              ) : (
                <>
                  <div
                    className="themed-scrollbar"
                    style={{ width: "100%", maxWidth: "100%" }}
                    ref={tableContainerRef}
                  >
                    <ThemedTable
                      columns={columns}
                      data={filteredTableData}
                      containerHeight="auto"
                      bodyHeight="45vh"
                      themeMode={themeMode}
                      currentTheme={currentTheme}
                      onCellEdit={handleCellChange}
                      enableCheckboxSelection
                      selectedRowIdx={selectedRowIdx}
                      selectedRowIds={selectedRowIds}
                      onRowSelect={handleRowSelect}
                      onSelectAll={handleSelectAll}
                      targetPage={targetPage}
                      onTargetPageReached={handleTargetPageReached}
                      itemsPerPageOptions={[5, 10, 20, 50]}
                      defaultItemsPerPage={10}
                    />
                                                                                            </div>
                </>
              )}
                                                                                          </div>
                                                                                        </div>
        ) : (
          <div className="call-automation-inner-root">
            <div
              className="ca-left-section"
              style={{
                display: "flex",
                flexDirection: "column",
                height: "73vh",
              }}
            >
              <div style={{ flex: 1, fontSize: "12px" }}>
                {/* 1. Download Template button */}
                <div style={{ margin: "2vh", zIndex: 9999 }}>
                  <Button
                    label="Download Template"
                    backgroundColor={
                      selectedBranch && selectedUsecase && selectedSubUsecase
                        ? currentTheme.callAutomationPage.activeButtonColor
                        : currentTheme.callAutomationPage.buttonColor
                    }
                    color={
                      selectedBranch && selectedUsecase && selectedSubUsecase
                        ? currentTheme.callAutomationPage.submitColor
                        : currentTheme.callAutomationPage.textColor
                    }
                    width="100%"
                    height="4vh"
                    borderRadius="5px"
                    onClick={handleDownloadTemplate}
                    disabled={loading}
                  />
                                                                                      </div>
                
                {/* 2. Branch selection */}
                <div
                  className="branch-selection-header"
                  style={{
                    background: currentTheme.settingPage.themeBackground,
                    color: currentTheme.settingPage.textColor,
                    padding: "10px",
                    borderRadius: "5px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    margin: "2vh",
                    position: "relative",
                    zIndex: 99,
                    border: missingSelections.branch ? "2px solid #ff4e3a" : "none",
                    boxShadow: missingSelections.branch ? "0 0 0 1px #ff4e3a" : "none",
                  }}
                >
                  <div className="custom-branch-dropdown-label">
                    Branch:
                                                                        </div>
                  <div
                    className={`custom-branch-dropdown-container${branchDropdownOpen ? ' open' : ''}${missingSelections.branch ? ' error' : ''}`}
                    tabIndex={100}
                    style={{
                      background: currentTheme.settingPage.themeBackground,
                      color: currentTheme.settingPage.textColor,
                    }}
                    onBlur={() => setBranchDropdownOpen(false)}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: loadingUseCases ? 'not-allowed' : 'pointer',
                        minHeight: '40px',
                        padding: '0 14px',
                        borderRadius: '8px',
                        opacity: loadingUseCases ? 0.7 : 1,
                        userSelect: 'none',
                      }}
                      onClick={() => !loadingUseCases && setBranchDropdownOpen((open) => !open)}
                    >
                      <span style={{ color: selectedBranch ? currentTheme.settingPage.textColor : '#888' }}>
                        {loadingUseCases
                          ? 'Loading branches...'
                          : (branchOptions.find(opt => opt.value === selectedBranch)?.label || 'Select an option')}
                      </span>
                      <span style={{ marginLeft: 8, display: 'flex', alignItems: 'center' }}>
                        <img
                          src={branchDropdownOpen
                            ? (themeMode === 'dark' ? '/icons/up-orange.png' : '/icons/up-orange.png')
                            : (themeMode === 'dark' ? '/icons/down.svg' : '/icons/down-black.png')}
                          alt="dropdown arrow"
                          style={{ width: 18, height: 18 }}
                        />
                      </span>
                                                                      </div>
                    {branchDropdownOpen && (
                      <div
                        className="custom-branch-dropdown-list"
                        style={{
                          background: currentTheme.settingPage.themeBackground,
                          zIndex: 10000,
                        }}
                      >
                        {branchOptions.map((option) => (
                          <div
                            key={option.value}
                            className={`custom-branch-dropdown-option${selectedBranch === option.value ? ' selected' : ''}`}
                            style={{
                              background: selectedBranch === option.value ? currentTheme.settingPage.button : 'inherit',
                              color: selectedBranch === option.value ? '#fff' : currentTheme.settingPage.textColor,
                            }}
                            onClick={() => {
                              setSelectedBranch(option.value);
                              setShowUsecaseSelection(true);
                              setMissingSelections(prev => ({ ...prev, branch: false }));
                              setBranchDropdownOpen(false);
                            }}
                          >
                            {option.label}
                                                                    </div>
                        ))}
                                                                  </div>
                    )}
                                                                </div>
                                                              </div>

                {/* 1.5 Type selection - Only show if validEhrs has values */}
                {validEhrs.length >= 0 && (
                  <div
                    className="branch-selection-header"
                    style={{
                      background: currentTheme.settingPage.themeBackground,
                      color: currentTheme.settingPage.textColor,
                      padding: "10px",
                      borderRadius: "5px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      margin: "2vh",
                      position: "relative",
                      zIndex: 10,
                    }}
                  >
                    <div className="custom-branch-dropdown-label">
                      Type:
                                                                                    </div>
                    <div
                      className={`custom-branch-dropdown-container${typeDropdownOpen ? ' open' : ''}`}
                      tabIndex={0}
                      style={{
                        background: currentTheme.settingPage.themeBackground,
                        color: currentTheme.settingPage.textColor,
                        position: 'relative',
                        zIndex: 3000,
                      }}
                      onBlur={() => setTypeDropdownOpen(false)}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          minHeight: '40px',
                          padding: '0 14px',
                          borderRadius: '8px',
                          userSelect: 'none',
                        }}
                        onClick={() => setTypeDropdownOpen((open) => !open)}
                      >
                        <span style={{ color: currentTheme.settingPage.textColor }}>
                          {selectedType === 'API' ? 'API' : 'Manual'}
                        </span>
                        <span style={{ marginLeft: 8, display: 'flex', alignItems: 'center' }}>
                          <img
                            src={typeDropdownOpen
                              ? (themeMode === 'dark' ? '/icons/up-orange.png' : '/icons/up-orange.png')
                              : (themeMode === 'dark' ? '/icons/down.svg' : '/icons/down-black.png')}
                            alt="dropdown arrow"
                            style={{ width: 18, height: 18 }}
                          />
                        </span>
                                                                                  </div>
                      {typeDropdownOpen && (
                        <div
                          className="custom-branch-dropdown-list"
                          style={{
                            background: currentTheme.settingPage.themeBackground,
                            position: 'absolute',
                            zIndex: 9999,
                            left: 0,
                            right: 0,
                            top: 'calc(100% + 6px)',
                          }}
                        >
                          {[
                            { value: 'manual', label: 'Manual' },
                            { value: 'API', label: 'API' },
                          ].map((option) => (
                            <div
                              key={option.value}
                              className={`custom-branch-dropdown-option${selectedType === option.value ? ' selected' : ''}`}
                              style={{
                                background: selectedType === option.value ? currentTheme.settingPage.button : 'inherit',
                                color: selectedType === option.value ? '#fff' : currentTheme.settingPage.textColor,
                              }}
                              onClick={() => {
                                const newType = option.value;
                                
                                // Validate selections before allowing API mode
                                if (newType === 'API') {
                                  if (!selectedBranch || !selectedUsecase || !selectedSubUsecase) {
                                    message.error('Please select Branch, Usecase, and Sub-usecase before choosing API mode');
                                    return;
                                  }
                                  setEhrPopupStep(1);
                                  // Auto-select EHR if only one available and skip to step 2
                                  // if (validEhrs.length === 1) {
                                  //   setSelectedEhr(validEhrs[0]);
                                  //   setEhrPopupStep(2);
                                  // } else {
                                  //   setEhrPopupStep(1);
                                  // }
                                  setShowEhrConfigPopup(true);
                                }
                                
                                setSelectedType(newType);
                                setTypeDropdownOpen(false);
                              }}
                            >
                              {option.label}
                                                                                </div>
                          ))}
                                                                              </div>
                      )}
                                                                            </div>
                                                                          </div>
                )}
                
                {/* 3. Usecase and subusecase */}
                {selectedBranch && (
                  <div
                    className="usecase-selection-header"
                    style={{
                      background: currentTheme.settingPage.themeBackground,
                      color: currentTheme.settingPage.textColor,
                      padding: "10px",
                      borderRadius: "5px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      margin: "2vh",
                      marginBottom: "10px",
                      position: "relative",
                      border: (missingSelections.usecase || missingSelections.subUsecase) ? "2px solid #ff4e3a" : "none",
                      boxShadow: (missingSelections.usecase || missingSelections.subUsecase) ? "0 0 0 1px #ff4e3a" : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "1rem",
                      }}
                    >
                      <strong>Usecase:</strong>
                      {selectedUsecase && (
                        <span
                          style={{
                            background: "#2d284d",
                            color: "#fff",
                            borderRadius: "4px",
                            padding: "2px 8px",
                            fontWeight: 600,
                            cursor: "pointer",
                            fontSize: "1rem",
                          }}
                          title={formatUsecaseName(selectedUsecase)}
                        >
                          {getAbbreviation(formatUsecaseName(selectedUsecase))}
                        </span>
                      )}
                      {selectedSubUsecase && (
                        <>
                          <span
                            style={{
                              marginLeft: "12px",
                              fontWeight: 600,
                              fontSize: "1rem",
                            }}
                          >
                            Subusecase:
                          </span>
                          <span
                            style={{
                              background: "#2d284d",
                              color: "#fff",
                              borderRadius: "4px",
                              padding: "2px 8px",
                              fontWeight: 600,
                              cursor: "pointer",
                              fontSize: "1rem",
                            }}
                            title={formatUsecaseName(selectedSubUsecase)}
                          >
                            {getAbbreviation(
                              formatUsecaseName(selectedSubUsecase)
                            )}
                          </span>
                        </>
                      )}
                                                            </div>
                    <Button
                      label={selectedUsecase ? "Change" : "Select"}
                      backgroundColor={currentTheme.settingPage.button}
                      color="#ffffff"
                      width="30%"
                      borderRadius="5px"
                      height="4vh"
                      onClick={() => setShowUsecaseSelection(true)}
                    />
                    {(missingSelections.usecase || missingSelections.subUsecase) && (
                      <div style={{
                        position: "absolute",
                        top: "100%",
                        left: "0",
                        right: "0",
                        fontSize: "12px",
                        color: "#ff4e3a",
                        fontWeight: "500",
                        marginTop: "4px",
                        zIndex: 10000,
                      }}>
                        Please select a usecase and sub-usecase to continue
                                                          </div>
                    )}
                                                        </div>
                )}
                
                {/* 3.5 Question Set Selection */}
                {selectedBranch && selectedUsecase && selectedSubUsecase && (
                  <div
                    className="question-set-selection-header"
                    style={{
                      background: currentTheme.settingPage.themeBackground,
                      color: currentTheme.settingPage.textColor,
                      padding: "10px",
                      borderRadius: "5px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      margin: "2vh",
                      position: "relative",
                      zIndex: 9,
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: "1rem" }}>
                      Question Set:
                                                      </div>
                    <div
                      className={`custom-branch-dropdown-container${questionSetDropdownOpen ? ' open' : ''}`}
                      tabIndex={0}
                      style={{
                        background: currentTheme.settingPage.themeBackground,
                        color: currentTheme.settingPage.textColor,
                        width: "60%",
                      }}
                      onBlur={() => setQuestionSetDropdownOpen(false)}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: loadingQuestionSets ? 'not-allowed' : 'pointer',
                          minHeight: '40px',
                          padding: '0 14px',
                          borderRadius: '8px',
                          opacity: loadingQuestionSets ? 0.7 : 1,
                          userSelect: 'none',
                        }}
                        onClick={() => !loadingQuestionSets && setQuestionSetDropdownOpen((open) => !open)}
                      >
                        <span style={{ 
                          color: selectedQuestionSet ? currentTheme.settingPage.textColor : '#888',
                          fontSize: "0.95rem"
                        }}>
                          {loadingQuestionSets
                            ? 'Loading question sets...'
                            : (selectedQuestionSet?.questionSetName || 'None')}
                        </span>
                        <span style={{ marginLeft: 8, display: 'flex', alignItems: 'center' }}>
                          <img
                            src={questionSetDropdownOpen
                              ? (themeMode === 'dark' ? '/icons/up-orange.png' : '/icons/up-orange.png')
                              : (themeMode === 'dark' ? '/icons/down.svg' : '/icons/down-black.png')}
                            alt="dropdown arrow"
                            style={{ width: 18, height: 18 }}
                          />
                        </span>
                                                    </div>
                      {questionSetDropdownOpen && (
                        <div className="custom-branch-dropdown-list" style={{ background: currentTheme.settingPage.themeBackground }}>
                          <div
                            className={`custom-branch-dropdown-option${!selectedQuestionSet ? ' selected' : ''}`}
                            style={{
                              background: !selectedQuestionSet ? currentTheme.settingPage.button : 'inherit',
                              color: !selectedQuestionSet ? '#fff' : currentTheme.settingPage.textColor,
                            }}
                            onClick={() => {
                              setSelectedQuestionSet(null);
                              setQuestionSetDropdownOpen(false);
                            }}
                          >
                            None
                                                  </div>
                          {questionSets.map((questionSet) => (
                            <div
                              key={questionSet._id}
                              className={`custom-branch-dropdown-option${selectedQuestionSet?._id === questionSet._id ? ' selected' : ''}`}
                              style={{
                                background: selectedQuestionSet?._id === questionSet._id ? currentTheme.settingPage.button : 'inherit',
                                color: selectedQuestionSet?._id === questionSet._id ? '#fff' : currentTheme.settingPage.textColor,
                              }}
                              onClick={() => {
                                setSelectedQuestionSet(questionSet);
                                setQuestionSetDropdownOpen(false);
                              }}
                            >
                              {questionSet.questionSetName}
                                                </div>
                          ))}
                                              </div>
                      )}
                                            </div>
                                          </div>
                )}
                
                {/* 4. Batch name (required) */}
                <div style={{ margin: "2vh", zIndex: 9999 }}>
                  <div style={{ 
                    display: "flex", 
                    flexDirection: "column",
                    gap: "8px"
                  }}>
                    <div style={{ 
                      display: "flex", 
                      alignItems: "center",
                      gap: "8px",
                      position: "relative"
                    }}>
                      <div style={{ position: "relative", width: "100%" }}>
                        <input
                          type="text"
                          value={batchAlias}
                          onChange={handleBatchAliasChange}
                          placeholder={showAnimation ? "" : "Enter batch name"}
                          style={{
                            width: "calc(100% - 1.7rem)",
                            height: "4vh",
                            padding: "0 12px",
                            borderRadius: "5px",
                            border: `2px solid ${
                              batchAliasError
                                ? "#ff4e3a"
                                : aliasValidation.isValid === false 
                                ? "#ff4e3a" 
                                : aliasValidation.isValid === true 
                                ? "#4caf50" 
                                : currentTheme.settingPage.borderColor || "#ddd"
                            }`,
                            backgroundColor: currentTheme.settingPage.themeBackground,
                            color: showAnimation ? "transparent" : currentTheme.settingPage.textColor,
                            fontSize: "14px",
                            outline: "none",
                            boxShadow: batchAliasError ? "0 0 0 1px #ff4e3a" : "none",
                            // Additional Chrome-specific fixes for animation overlay
                            ...(showAnimation && {
                              caretColor: "transparent",
                              textShadow: "none",
                              userSelect: "none",
                              WebkitTextFillColor: "transparent",
                              WebkitUserSelect: "none"
                            })
                          }}
                          disabled={loading || showAnimation}
                        />
                        {showAnimation && (
                          <div
                            style={{
                              position: "absolute",
                              top: "0",
                              left: "0",
                              right: "0",
                              bottom: "0",
                              display: "flex",
                              alignItems: "center",
                              padding: "0 12px",
                              pointerEvents: "none",
                              backgroundColor: "transparent",
                              zIndex: 10,
                              borderRadius: "5px",
                              overflow: "hidden"
                            }}
                          >
                            <AIGeneratedText
                              text={animatedAliasText}
                              isAnimating={isAnimatingAlias}
                              onComplete={handleAnimationComplete}
                              style={{
                                color: currentTheme.settingPage.textColor,
                                fontSize: "14px",
                                width: "100%",
                              }}
                            />
                                        </div>
                        )}
                                      </div>
                      <ThemedTooltip
                        title={
                          !selectedBranch || !selectedUsecase || !selectedSubUsecase
                            ? "Select usecases first"
                            : "Generate with AI"
                        }
                        placement="top"
                      >
                        <div>
                          <Button
                            label={
                              isGeneratingAlias ? (
                                "..."
                              ) : (
                                <img
                                  src="/icons/custom/ai-generate.png"
                                  alt="AI Generate"
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    filter: (!selectedBranch || !selectedUsecase || !selectedSubUsecase || loading || isGeneratingAlias || showAnimation)
                                      ? "grayscale(100%) opacity(0.5)"
                                      : "none"
                                  }}
                                />
                              )
                            }
                            backgroundColor={currentTheme.callAutomationPage.activeButtonColor}
                            color={currentTheme.callAutomationPage.submitColor}
                            width="50px"
                            height="4vh"
                            borderRadius="5px"
                            onClick={handleGenerateBatchAlias}
                            disabled={
                              !selectedBranch || 
                              !selectedUsecase || 
                              !selectedSubUsecase || 
                              loading || 
                              isGeneratingAlias ||
                              showAnimation
                            }
                          />
                                    </div>
                      </ThemedTooltip>
                                  </div>
                    {(aliasValidation.isChecking || aliasValidation.message) && (
                      <div style={{
                        fontSize: "12px",
                        color: aliasValidation.isChecking 
                          ? currentTheme.settingPage.textColor 
                          : aliasValidation.isValid === false 
                          ? "#ff4e3a" 
                          : "#4caf50",
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px"
                      }}>
                        {aliasValidation.isChecking && (
                          <span>⏳</span>
                        )}
                        {aliasValidation.message}
                                </div>
                    )}
                              </div>
                            </div>
                
                {/* 5. Select Date */}
                <div style={{ margin: "2vh", zIndex: 9999 }}>
                  <SimpleDatePicker
                    selectedDate={selectedDate}
                    onChange={setSelectedDate}
                    placeholder="Select Date"
                    className={themeMode}
                  />
                          </div>
                
                {/* 6. Select Time */}
                <div style={{ margin: "2vh", zIndex: 9999 }}>
                  <SimpleTimePicker
                    selectedTime={selectedTime}
                    onChange={handleTimeSlotSelect}
                    placeholder="Select Time"
                    slots={availableSlots}
                    loading={loadingSlots}
                    className={themeMode}
                  />
                        </div>
                
                {/* 7. Custom Questions */}
                {selectedUsecase && (
                  <div
                    className="question-selection-header"
                    style={{
                      background: currentTheme.settingPage.themeBackground,
                      color: currentTheme.settingPage.textColor,
                      padding: "10px",
                      borderRadius: "5px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      margin: "1rem",
                    }}
                  >
                    <div>
                      <strong>Custom Questions:</strong>{" "}
                      {selectedQuestions.length} selected
                      </div>
                    <Button
                      label={selectedQuestions.length > 0 ? "Change" : "Select"}
                      backgroundColor={currentTheme.settingPage.button}
                      color="#ffffff"
                      width="30%"
                      borderRadius="5px"
                      height="4vh"
                      onClick={() => setShowQuestionSelection(true)}
                    />
                    </div>
                )}
                  </div>
              <div style={{ marginTop: "auto" }}>
                <span
                  style={{ display: "flex", justifyContent: "space-evenly" }}
                >
                  <Button
                    label={"Reset"}
                    backgroundColor={
                      currentTheme.callAutomationPage.buttonColor
                    }
                    color={currentTheme.callAutomationPage.textColor}
                    width={"29%"}
                    borderRadius={"5px"}
                    height={"4vh"}
                    onClick={resetData}
                  />
                  <Button
                    label={"Cancel"}
                    backgroundColor={
                      currentTheme.callAutomationPage.buttonColor
                    }
                    color={currentTheme.callAutomationPage.textColor}
                    width={"29%"}
                    borderRadius={"5px"}
                    height={"4vh"}
                    onClick={handleCancel}
                  />
                  <Button
                    label={"Continue"}
                    backgroundColor={
                      submitValidate()
                        ? currentTheme.callAutomationPage.activeButtonColor
                        : currentTheme.callAutomationPage.buttonColor
                    }
                    color={
                      submitValidate()
                        ? currentTheme.callAutomationPage.submitColor
                        : currentTheme.callAutomationPage.textColor
                    }
                    width={"29%"}
                    height={"4vh"}
                    borderRadius={"5px"}
                    disabled={!submitValidate() || loading}
                    onClick={handleContinue}
                  />
                </span>
              </div>
            </div>
            <div className="ca-right-section">
              <div className="cars-page-one" ref={dropAreaRef}>
                {loading && selectedType === 'API' ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      width: "100%",
                      gap: "16px",
                    }}
                  >
                    <LogoLoader />
                    <div
                      style={{
                        textAlign: "center",
                        color: currentTheme.homePage.textColor,
                        fontSize: "15px",
                        fontWeight: "500",
                      }}
                    >
                      Fetching EHR Data...
                    </div>
                  </div>
                ) : ehrNoDataMessage ? (
                  // Show no data message when EHR returns empty results
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      width: "100%",
                      gap: "24px",
                      padding: "40px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "48px",
                        opacity: 0.3,
                      }}
                    >
                      📊
                    </div>
                    <div
                      style={{
                        textAlign: "center",
                        color: currentTheme.homePage.textColor,
                        fontSize: "18px",
                        fontWeight: "600",
                        maxWidth: "400px",
                      }}
                    >
                      {ehrNoDataMessage}
                    </div>
                    <div
                      style={{
                        textAlign: "center",
                        color: currentTheme.text?.secondary || currentTheme.homePage.textColor,
                        fontSize: "14px",
                        opacity: 0.7,
                        maxWidth: "400px",
                      }}
                    >
                      Try adjusting your date range or EHR configuration to fetch data.
                    </div>
                  </div>
                ) : continueClicked ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      width: "100%",
                    }}
                  >
                    {/* Add Manually Button - Only show for manual mode */}
                    {selectedType === 'manual' && (
                      <>
                        <div
                          style={{
                            padding: "20px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "20px",
                            marginRight: "-1rem",
                            marginTop: "-3.2vh",
                          }}
                        >
                          <Button
                            label="Add Manually"
                            backgroundColor={
                              currentTheme.callAutomationPage.activeButtonColor
                            }
                            color={currentTheme.callAutomationPage.submitColor}
                            width="200px"
                            height="4vh"  
                            borderRadius="5px"
                            onClick={handleAddManually}
                            disabled={
                              !selectedBranch ||
                              !selectedUsecase ||
                              !selectedSubUsecase ||
                              loading
                            }
                          />
                        </div>
                        {/* File Uploader */}
                        <div style={{ flex: 1 }}>
                          <FileUploader
                            files={files}
                            setFiles={setFiles}
                            onError={handleError}
                            setTableData={setTableDataWithIds}
                            onSubmit={handleFileSubmit}
                            branch={selectedBranch}
                            usecaseName={selectedUsecase}
                            subUseCase={selectedSubUsecase}
                            themeMode={themeMode}
                            currentTheme={currentTheme}
                            isTrialUser={isTrialUser}
                            onTrialDataLimit={handleTrialDataLimit}
                          />
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <span>Section will load after data is uploaded</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <CustomDialog
        open={isConfirmationOpen}
        message={`Are you sure you want to continue with this file: ${filename}?`}
        onConfirm={handleConfirmed}
        onCancel={handleConfirmationClose}
        themeMode={themeMode}
        currentTheme={currentTheme}
      />
      <UseCaseSelection
        isOpen={showUsecaseSelection}
        onClose={() => setShowUsecaseSelection(false)}
        onConfirm={handleUsecaseSelect}
        selectedBranch={selectedBranch}
        useCases={useCases}
        loading={loadingUseCases}
      />
      <QuestionSelection
        isOpen={showQuestionSelection}
        onClose={() => setShowQuestionSelection(false)}
        onConfirm={handleQuestionSelect}
        selectedBranch={selectedBranch}
        selectedUsecase={selectedUsecase}
        selectedSubUsecase={selectedSubUsecase}
        selectedQuestions={selectedQuestions}
        setSelectedQuestions={setSelectedQuestions}
      />
      <CustomDialog
        open={scheduleResult.open}
        message={scheduleResult.message}
        onConfirm={handleCloseScheduleResult}
        onCancel={handleCloseScheduleResult}
        themeMode={themeMode}
        currentTheme={currentTheme}
      />
      <CustomDialog
        open={showAddEhrConfirm}
        message={`This EHR is not configured for your account. Do you want to add it?`}
        onConfirm={() => {
          setShowAddEhrConfirm(false);
          navigate(`${ROUTES.EHR_MANAGEMENT.path}?addEhr=${encodeURIComponent(ehrToAdd)}`);
          setEhrToAdd("");
        }}
        onCancel={() => {
          setShowAddEhrConfirm(false);
          setEhrToAdd("");
        }}
        themeMode={themeMode}
        currentTheme={currentTheme}
      />

      {/* Billing Restriction Popups */}
      <BillingRestrictionPopup
        visible={showAccessRestrictionPopup}
        type="access"
        message={accessRestrictionMessage}
        onClose={() => setShowAccessRestrictionPopup(false)}
        showBackButton={true}
      />

      <BillingRestrictionPopup
        visible={showUnitsWarningPopup}
        type="warning"
        message={unitsWarningMessage}
        onClose={() => setShowUnitsWarningPopup(false)}
        showBackButton={false}
      />

      {/* <BillingRestrictionPopup
        visible={showExpiredUnitsPopup}
        type="expired_units"
        message={expiredUnitsMessage}
        onClose={() => setShowExpiredUnitsPopup(false)}
        headline="Account units have expired"
        primaryButtonText="Continue anyway"
        secondaryButtonText="Go back"
        showBackButton={false}
        onPrimaryAction={() => {
          sessionStorage.setItem('acknowledgedExpiredUnits', 'true');
          setShowExpiredUnitsPopup(false);
        }}
        onSecondaryAction={() => window.history.back()}
      /> */}

      {/* Trial Plan Popups */}
      <BillingRestrictionPopup
        visible={showTrialWelcomePopup}
        type="warning"
        message={`You are currently on a trial plan with limited calling minutes. You can create as many batches as you need, but are limited to a total of ${TRIAL_USER_PATIENT_LIMIT} calls across all active batches. Please ask your administrator to upgrade your plan for additional features.`}
        onClose={() => setShowTrialWelcomePopup(false)}
        showBackButton={false}
      />

      <BillingRestrictionPopup
        visible={showTrialDataLimitPopup}
        type="warning"
        message={trialDataLimitMessage}
        onClose={() => setShowTrialDataLimitPopup(false)}
        showBackButton={false}
      />

      <BillingRestrictionPopup
        visible={showTrialBatchLimitPopup}
        type="access"
        message={trialBatchLimitMessage}
        onClose={() => {
          setShowTrialBatchLimitPopup(false);
          navigate("/home");
        }}
        showBackButton={false}
      />

      {/* EHR Configuration Popup */}
      {showEhrConfigPopup && (
        <>
          <style>
            {`
              /* Position date picker calendar on top */
              .date-picker-open-top input[type="date"]::-webkit-calendar-picker-indicator {
                position: absolute;
                top: auto;
                bottom: 100%;
              }
              
              /* For browsers that support it, position the calendar popup on top */
              .date-picker-open-top input[type="date"] {
                direction: rtl;
              }
              
              .date-picker-open-top input[type="date"]::-webkit-datetime-edit {
                direction: ltr;
              }
            `}
          </style>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000,
            }}
            onClick={() => {
              setShowEhrConfigPopup(false);
              setSelectedType('manual'); // Reset to manual if popup is closed
              setEhrPopupStep(1);
            }}
          >
            <div
            style={{
            backgroundColor: themeMode === 'dark' ? '#04002b' : '#ffffff',
            color: currentTheme.homePage.textColor,
            borderRadius: '1.25rem',
            padding: '0 2.5rem 2rem 2.5rem',
            maxWidth: '72rem',
            width: '96%',
            maxHeight: '95vh',
            overflow: 'hidden',
            boxShadow: themeMode === 'dark'
              ? '0 1.25rem 2.5rem rgba(0, 0, 0, 0.6), 0 0 0 0.0625rem rgba(255, 255, 255, 0.1)'
              : '0 1.25rem 2.5rem rgba(0, 0, 0, 0.15), 0 0 0 0.0625rem rgba(0, 0, 0, 0.05)',
            border: themeMode === 'dark' ? '0.0625rem solid #302d4c' : '0.0625rem solid #e9e8ea',
            }}
              onClick={(e) => e.stopPropagation()}
            >
            {/* Header */}
            <div
              style={{
                padding: '1.5rem 1.5rem 0 1.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                borderBottom: `0.0625rem solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
                paddingBottom: '1rem',
                marginBottom: '1.5rem',
              }}
            >
              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: currentTheme.homePage.textColor,
                  margin: '0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
              >
                EHR Configuration
              </h2>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  color: currentTheme.homePage.textColor,
                  cursor: 'pointer',
                  padding: '0.25rem',
                  borderRadius: '0.25rem',
                  opacity: 0.7,
                  transition: 'opacity 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '2rem',
                  height: '2rem',
                }}
                onClick={() => {
                  setShowEhrConfigPopup(false);
                  setSelectedType('manual');
                  setEhrPopupStep(1);
                }}
                onMouseEnter={(e) => (e.target.style.opacity = '1')}
                onMouseLeave={(e) => (e.target.style.opacity = '0.7')}
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div
              style={{
                padding: '0 2.5rem',
                marginBottom: '2rem',
                overflowY: 'auto',
                scrollbarGutter: 'stable',
                maxHeight: 'calc(95vh - 9rem)',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              {ehrPopupStep === 1 ? (
                <>
                  {/* Step 1: EHR Card Selection */}
                  <label
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      marginBottom: '1rem',
                      display: 'block',
                      color: currentTheme.homePage.textColor,
                    }}
                  >
                    Select EHR System
                  </label>
                  <div
                  style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: '0.75rem',
                  }}
                  >
                    {SORTED_EHRS_BY_TYPE.map((ehr, index) => {
                      const isTypeBreak =
                        index > 0 &&
                        SORTED_EHRS_BY_TYPE[index - 1].type !== ehr.type;
                      const isActuallyAvailable = availableEhrs.some(
                        (v) => v.toLowerCase() === ehr.key.toLowerCase()
                      );
                      const isAvailable = isActuallyAvailable;
                      return (
                        <div key={ehr.key} style={{ display: 'contents' }}>
                          {isTypeBreak && (
                            <div
                              style={{
                                gridColumn: '1 / -1',
                                height: '1.5rem',
                              }}
                            />
                          )}
                          <div
                            onClick={() => {
                              if (!isActuallyAvailable) {
                                setEhrToAdd(ehr.key);
                                setShowAddEhrConfirm(true);
                                return;
                              }
                              if (!isAvailable) return;
                              setSelectedEhr(ehr.key);
                              setEhrDynamicFields({});
                              setSelectedEhrFacilityId("");
                              setEhrPopupStep(2);
                            }}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.875rem',
                              padding: '1.5rem .5rem',
                              borderRadius: '0.75rem',
                              border: '0.0625rem solid #e2e8f0',
                              backgroundColor: '#ffffff',
                              cursor: isAvailable ? 'pointer' : 'not-allowed',
                              opacity: isAvailable ? 1 : 1,
                              filter: isAvailable ? 'none' : 'none',
                              transition: 'all 0.2s ease',
                              boxShadow: themeMode === 'dark'
                                ? '0 0.125rem 0.5rem rgba(0, 0, 0, 0.3)'
                                : '0 0.125rem 0.5rem rgba(0, 0, 0, 0.06)',
                              position: 'relative',
                            }}
                            onMouseEnter={(e) => {
                              if (isAvailable) {
                                e.currentTarget.style.borderColor = currentTheme.callAutomationPage.activeButtonColor;
                                e.currentTarget.style.boxShadow = themeMode === 'dark'
                                  ? '0 0.25rem 1rem rgba(0, 0, 0, 0.4)'
                                  : '0 0.25rem 1rem rgba(0, 0, 0, 0.12)';
                                e.currentTarget.style.transform = 'translateY(-0.125rem)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#e2e8f0';
                              e.currentTarget.style.boxShadow = themeMode === 'dark'
                                ? '0 0.125rem 0.5rem rgba(0, 0, 0, 0.3)'
                                : '0 0.125rem 0.5rem rgba(0, 0, 0, 0.06)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <div
                              style={{
                                width: '100%',
                                height: '3.75rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'transparent',
                                border: 'none',
                                borderRadius: '0.5rem',
                                // padding: '0.625rem 1rem',
                              }}
                            >
                              <img
                                src={ehr.logo}
                                alt={ehr.displayName}
                                style={{
                                  maxWidth: ehr.magnifyby
                                    ? `${9 * (1 + ehr.magnifyby / 100)}rem`
                                    : '9rem',
                                  maxHeight: ehr.magnifyby
                                    ? `${4.75 * (1 + ehr.magnifyby / 100)}rem`
                                    : '4.75rem',
                                  width: 'auto',
                                  height: 'auto',
                                  objectFit: 'contain',
                                }}
                              />
                            </div>
                            <span
                              style={{
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                color: '#1e293b',
                                textAlign: 'center',
                                lineHeight: '1.3',
                              }}
                            >
                              {ehr.displayName}
                            </span>
                            {/* {!isAvailable && (
                              <span
                                style={{
                                  position: 'absolute',
                                  top: '0.375rem',
                                  right: '0.375rem',
                                  fontSize: '0.625rem',
                                  color: themeMode === 'dark' ? '#888' : '#64748b',
                                  backgroundColor: themeMode === 'dark' ? '#2a2a3e' : '#cbd5e1',
                                  padding: '0.125rem 0.375rem',
                                  borderRadius: '0.25rem',
                                }}
                              >
                                N/A
                              </span>
                            )} */}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <>
                  {/* Step 2: EHR Configuration Fields */}
                  {/* Selected EHR Header with Back Button */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      marginBottom: '1.25rem',
                      paddingBottom: '1rem',
                      borderBottom: `0.0625rem solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
                    }}
                  >
                    <button
                      onClick={() => {
                        setEhrPopupStep(1);
                        setSelectedEhr('');
                        setEhrDynamicFields({});
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '1.25rem',
                        color: currentTheme.homePage.textColor,
                        cursor: 'pointer',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.375rem',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'background-color 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = themeMode === 'dark' ? '#302d4c' : '#f0f0f0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      ←
                    </button>
                    <span
                      style={{
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: currentTheme.homePage.textColor,
                      }}
                    >
                      {ALL_EHRS.find((e) => e.key === selectedEhr)?.displayName || selectedEhr}
                    </span>
                  </div>

              {/* Dynamic Fields Rendering based on EHR Config */}
              {selectedEhr && selectedUsecase && (() => {
                // Check if EHR is accessible (configured in facilities or validEhrs)
                const isEhrAccessible = availableEhrs.some(
                  (v) => v.toLowerCase() === selectedEhr.toLowerCase()
                );
                
                if (!isEhrAccessible) {
                  return (
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#f8d7da', 
                      color: '#721c24',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      fontSize: '14px',
                      border: '1px solid #f5c6cb'
                    }}>
                      <strong>Access Restricted:</strong> {ALL_EHRS.find((e) => e.key === selectedEhr)?.displayName || selectedEhr} is not configured for your account. Please contact support to enable this EHR integration.
                    </div>
                  );
                }
                
                // Check if EHR configuration exists for this use case
                const ehrConfigExists = ehrConfig?.[selectedEhr]?.[selectedUsecase];
                if (!ehrConfigExists) {
                  return (
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#f8d7da', 
                      color: '#721c24',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      fontSize: '14px',
                      border: '1px solid #f5c6cb'
                    }}>
                      <strong>Configuration Missing:</strong> {ALL_EHRS.find((e) => e.key === selectedEhr)?.displayName || selectedEhr} is not configured for {selectedUsecase.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}. This combination is not yet supported for automated calls.
                    </div>
                  );
                }
                
                const usecaseConfig = ehrConfig[selectedEhr][selectedUsecase];
                
                if (!usecaseConfig.supported) {
                  return (
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#f8d7da', 
                      color: '#721c24',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      fontSize: '14px',
                      border: '1px solid #f5c6cb'
                    }}>
                      <strong>Use Case Not Supported:</strong> {selectedUsecase.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} is not configured for {ALL_EHRS.find((e) => e.key === selectedEhr)?.displayName || selectedEhr}. Please select a different use case or EHR system.
                    </div>
                  );
                }

                const allFields = [
                  ...(usecaseConfig.required_fields || []),
                  ...(usecaseConfig.optional_fields || [])
                ];

                // Facilities that have this EHR configured
                const facilitiesWithEhr = facilities.filter((f) => {
                  const mappings = f.ehrMappings || {};
                  return Object.keys(mappings).some((k) => {
                    const normalized = ALL_EHRS.find((e) => e.key.toLowerCase() === k.toLowerCase())?.key || k;
                    return normalized.toLowerCase() === selectedEhr.toLowerCase();
                  });
                });
                const facilityOptions = facilitiesWithEhr.map((f) => ({
                  label: f.facilityName || f._id,
                  value: f._id,
                }));

                // Helper function to render field label
                const getFieldLabel = (field) => {
                  return field.split('_').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ');
                };

                // Helper function to check if field is required
                const isRequired = (field) => {
                  return (usecaseConfig.required_fields || []).includes(field);
                };

                return (
                  <>
                    {facilityOptions.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <label style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          marginBottom: '8px',
                          display: 'block',
                          color: currentTheme.homePage.textColor,
                        }}>
                          Select Facility <span style={{ color: '#ff4e3a' }}>*</span>
                        </label>
                        <Dropdown
                          options={facilityOptions}
                          value={facilityOptions.find((o) => o.value === selectedEhrFacilityId) || null}
                          onChange={(opt) => setSelectedEhrFacilityId(opt?.value || "")}
                          placeholder="Select facility"
                          themeMode={themeMode}
                          isSearchable
                          width="100%"
                        />
                      </div>
                    )}
                    {allFields.map(field => {
                      // Date fields
                      if (field === 'start_date' || field === 'end_date' || field === 'as_of_date' || field === 'date_of_service') {
                        return (
                          <div key={field} style={{ marginBottom: '20px' }}>
                            <label style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              marginBottom: '8px',
                              display: 'block',
                              color: currentTheme.homePage.textColor,
                            }}>
                              {getFieldLabel(field)} {isRequired(field) && <span style={{ color: '#ff4e3a' }}>*</span>}
                              <span style={{ marginLeft: '0.375rem', fontSize: '0.75rem', opacity: 0.8 }}>(MM/DD/YYYY)</span>
                            </label>
                            {(field === 'start_date' || field === 'end_date') ? (
                              <div style={{ width: '100%' }}>
                                <SimpleDatePicker
                                  selectedDate={field === 'start_date' ? ehrStartDate : ehrEndDate}
                                  onChange={(value) => {
                                    if (field === 'start_date') setEhrStartDate(value);
                                    else setEhrEndDate(value);
                                  }}
                                  placeholder="MM/DD/YYYY"
                                  className={`${themeMode} ehr-config-date-picker`}
                                  dropdownPosition="bottom"
                                  dateFormat="MM/DD/YYYY"
                                  allowPastDates={true}
                                  // dropdownScale={0.8}
                                  matchInputWidth={false}
                                  dropdownWidth="50%"
                                  usePortal={true}
                                  style={{
                                    width: '100%',
                                    minWidth: '0',
                                    padding: '0.75rem',
                                    borderRadius: '0.5rem',
                                    border: `0.0625rem solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
                                    background: themeMode === 'dark' ? '#1a1a2e' : '#ffffff',
                                    color: currentTheme.homePage.textColor,
                                    fontSize: '0.875rem',
                                    boxSizing: 'border-box',
                                  }}
                                />
                              </div>
                            ) : (
                              <input
                                type="date"
                                value={ehrDynamicFields[field] || ''}
                                onChange={(e) => {
                                  setEhrDynamicFields(prev => ({ ...prev, [field]: e.target.value }));
                                }}
                                style={{
                                  width: '100%',
                                  padding: '12px',
                                  borderRadius: '8px',
                                  border: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
                                  backgroundColor: themeMode === 'dark' ? '#1a1a2e' : '#ffffff',
                                  color: currentTheme.homePage.textColor,
                                  fontSize: '14px',
                                  outline: 'none',
                                }}
                              />
                            )}
                          </div>
                        );
                      }

                      // Dropdown fields (office_id, location_id, provider_id, referring_provider_id, document_type_id, appointment_type_id, department_id)
                      if ([
                        'office_id',
                        'location_id',
                        'provider_id',
                        'referring_provider_id',
                        'document_type_id',
                        'appointment_type_id',
                        'department_id',
                      ].includes(field)) {
                        const dropdownData = ehrDropdownData[field] || [];
                        const isLoading = loadingEhrDropdowns[field];
                        const normalizeDropdownSelection = (selected) => {
                          if (selected === undefined || selected === null) return "";
                          if (typeof selected === "object" && !Array.isArray(selected)) {
                            const valueFromObject =
                              selected.value ??
                              selected.id ??
                              selected.target?.value ??
                              selected.currentTarget?.value ??
                              selected.option?.value ??
                              selected.data?.value ??
                              selected.selectedOption?.value;
                            return valueFromObject !== undefined && valueFromObject !== null
                              ? `${valueFromObject}`
                              : "";
                          }
                          return `${selected}`;
                        };
                        const currentDropdownValue = normalizeDropdownSelection(ehrDynamicFields[field]);
                        const parsedDropdownOptions = dropdownData.map((item) => {
                          let optionValue = "";
                          let optionLabel = "";

                          if (field === "office_id") {
                            optionValue = item.office_id;
                            optionLabel = item.name;
                          } else if (field === "location_id") {
                            optionValue = item.id;
                            optionLabel = `${item.name} (${item.abbreviation || item.city || ""})`;
                          } else if (field === "provider_id" || field === "referring_provider_id") {
                            optionValue = item.provider_id;
                            optionLabel = `${item.first_name || ""} ${item.last_name || ""} ${item.npi ? "(NPI: " + item.npi + ")" : ""}`.trim();
                          } else if (field === "document_type_id") {
                            // Response uses lowercase 'documenttypeid'
                            optionValue = item.documenttypeid || item.document_type_id || item.id;
                            optionLabel = item.name || item.description || item.type_name;
                          } else if (field === "appointment_type_id") {
                            // Athena BV appointment types
                            optionValue = item.appointment_type_id || item.id;
                            optionLabel = item.name || item.description || item.appointment_type_name;
                          } else if (field === "department_id") {
                            // Athena prior auth departments
                            optionValue = item.department_id || item.id;
                            optionLabel = item.name || item.department_name;
                          }

                          return {
                            value: optionValue !== undefined && optionValue !== null ? `${optionValue}` : "",
                            label: optionLabel || "",
                          };
                        });

                        return (
                          <div key={field} style={{ marginBottom: '20px' }}>
                            <label style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              marginBottom: '8px',
                              display: 'block',
                              color: currentTheme.homePage.textColor,
                            }}>
                              {getFieldLabel(field)} {isRequired(field) && <span style={{ color: '#ff4e3a' }}>*</span>}
                            </label>
                            <Dropdown
                              options={parsedDropdownOptions}
                              value={parsedDropdownOptions.find((option) => option.value === currentDropdownValue) || null}
                              onChange={(option) => {
                                const selectedValue = normalizeDropdownSelection(option);
                                const updates = { [field]: selectedValue };
                                if (field === "department_id") {
                                  const selectedDept = dropdownData.find(
                                    (item) =>
                                      `${item.department_id || item.id}` === selectedValue
                                  );
                                  updates.state = selectedDept?.address?.state ?? null;
                                }
                                setEhrDynamicFields((prev) => ({ ...prev, ...updates }));
                              }}
                              placeholder={
                                isLoading
                                  ? `Loading ${getFieldLabel(field)}...`
                                  : `Select ${getFieldLabel(field)}...`
                              }
                              themeMode={themeMode}
                              isSearchable
                              disabled={isLoading}
                              width="100%"
                            />
                          </div>
                        );
                      }

                      // Number fields
                      if (field === 'aging_days') {
                        return (
                          <div key={field} style={{ marginBottom: '20px' }}>
                            <label style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              marginBottom: '8px',
                              display: 'block',
                              color: currentTheme.homePage.textColor,
                            }}>
                              {getFieldLabel(field)} {isRequired(field) && <span style={{ color: '#ff4e3a' }}>*</span>}
                            </label>
                            <input
                              type="number"
                              value={ehrDynamicFields[field] || ''}
                              onChange={(e) => setEhrDynamicFields(prev => ({ 
                                ...prev, 
                                [field]: e.target.value ? parseInt(e.target.value) : '' 
                              }))}
                              placeholder="e.g., 60"
                              style={{
                                width: '100%',
                                padding: '12px',
                                borderRadius: '8px',
                                border: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
                                backgroundColor: themeMode === 'dark' ? '#1a1a2e' : '#ffffff',
                                color: currentTheme.homePage.textColor,
                                fontSize: '14px',
                                outline: 'none',
                              }}
                            />
                          </div>
                        );
                      }

                      // Boolean fields (include_payment_claims, document_reference, etc.)
                      if (field === 'include_payment_claims' || field === 'document_reference') {
                        return (
                          <div key={field} style={{ marginBottom: '20px' }}>
                            <label style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              marginBottom: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              color: currentTheme.homePage.textColor,
                            }}>
                              <input
                                type="checkbox"
                                checked={ehrDynamicFields[field] || false}
                                onChange={(e) => setEhrDynamicFields(prev => ({ 
                                  ...prev, 
                                  [field]: e.target.checked 
                                }))}
                                style={{ marginRight: '8px', cursor: 'pointer' }}
                              />
                              {getFieldLabel(field)} {isRequired(field) && <span style={{ color: '#ff4e3a', marginLeft: '4px' }}>*</span>}
                            </label>
                          </div>
                        );
                      }

                      // Array fields (patient_ids)
                      if (field === 'patient_ids') {
                        return (
                          <div key={field} style={{ marginBottom: '20px' }}>
                            <label style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              marginBottom: '8px',
                              display: 'block',
                              color: currentTheme.homePage.textColor,
                            }}>
                              {getFieldLabel(field)} {isRequired(field) && <span style={{ color: '#ff4e3a' }}>*</span>}
                            </label>
                            <textarea
                              value={rawPatientIds}
                              onChange={(e) => {
                                let newValue = e.target.value;
                                // Sanitize: allow only single comma between values — collapse multiple consecutive
                                // commas (with optional spaces) into one. Prevents backspace bug when user types ", , , ,"
                                newValue = newValue.replace(/,\s*,+/g, ', ');

                                setRawPatientIds(newValue);
                                const idsArray = newValue.split(',').map(id => id.trim());
                                setEhrDynamicFields(prev => ({ ...prev, [field]: idsArray }));
                              }}
                              onBlur={() => {
                                const cleanArray = rawPatientIds
                                  .split(',')
                                  .map(id => id.trim())
                                  .filter(id => id !== "");
                                setEhrDynamicFields(prev => ({ ...prev, [field]: cleanArray }));
                                setRawPatientIds(cleanArray.join(', '));
                              }}
                              placeholder="Enter patient IDs separated by commas (e.g., patient1, patient2)"
                              rows={3}
                              style={{
                                width: '100%',
                                padding: '12px',
                                borderRadius: '8px',
                                border: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
                                backgroundColor: themeMode === 'dark' ? '#1a1a2e' : '#ffffff',
                                color: currentTheme.homePage.textColor,
                                fontSize: '14px',
                                outline: 'none',
                                resize: 'vertical',
                              }}
                            />
                          </div>
                        );
                      }

                      // Default text field for other fields
                      return (
                        <div key={field} style={{ marginBottom: '20px' }}>
                          <label style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            marginBottom: '8px',
                            display: 'block',
                            color: currentTheme.homePage.textColor,
                          }}>
                            {getFieldLabel(field)} {isRequired(field) && <span style={{ color: '#ff4e3a' }}>*</span>}
                          </label>
                          <input
                            type="text"
                            value={ehrDynamicFields[field] || ''}
                            onChange={(e) => setEhrDynamicFields(prev => ({ ...prev, [field]: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              borderRadius: '8px',
                              border: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
                              backgroundColor: themeMode === 'dark' ? '#1a1a2e' : '#ffffff',
                              color: currentTheme.homePage.textColor,
                              fontSize: '14px',
                              outline: 'none',
                            }}
                          />
                        </div>
                      );
                    })}
                  </>
                );
              })()}

              {/* Date Range Quick Chips - Only show if start_date and end_date are in config */}
              {selectedEhr && ehrConfig?.[selectedEhr]?.[selectedUsecase]?.supported && 
               (ehrConfig[selectedEhr][selectedUsecase].required_fields?.includes('start_date') || 
                ehrConfig[selectedEhr][selectedUsecase].required_fields?.includes('end_date') ||
                ehrConfig[selectedEhr][selectedUsecase].optional_fields?.includes('start_date') ||
                ehrConfig[selectedEhr][selectedUsecase].optional_fields?.includes('end_date')) && (
                <div style={{ marginBottom: '20px' }}>
                  <label
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '8px',
                      display: 'block',
                      color: currentTheme.homePage.textColor,
                    }}
                  >
                    Quick Date Selection
                  </label>
                  
                  {/* Quick chips */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[
                      { label: 'Past 1 Day', days: 1 },
                      { label: 'Past 3 Days', days: 3 },
                      { label: 'Past 7 Days', days: 7 },
                      { label: 'Past 30 Days', days: 30 },
                    ].map((chip) => (
                      <button
                        key={chip.days}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
                          backgroundColor: themeMode === 'dark' ? '#1a1a2e' : '#f5f5f5',
                          color: currentTheme.homePage.textColor,
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onClick={() => {
                          const end = new Date();
                          const start = new Date();
                          start.setDate(start.getDate() - chip.days);
                          setEhrStartDate(start);
                          setEhrEndDate(end);
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = currentTheme.callAutomationPage.activeButtonColor;
                          e.target.style.color = '#ffffff';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = themeMode === 'dark' ? '#1a1a2e' : '#f5f5f5';
                          e.target.style.color = currentTheme.homePage.textColor;
                        }}
                      >
                        {chip.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
                </>
              )}
            </div>

            {/* Footer */}
            <div
              style={{
                padding: '0 1.5rem 1.5rem 1.5rem',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.75rem',
              }}
            >
              <button
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '5rem',
                  height: '2.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: themeMode === 'dark' ? '#302d4c' : '#e9e8ea',
                  color: currentTheme.homePage.textColor,
                }}
                onClick={() => {
                  setShowEhrConfigPopup(false);
                  setSelectedType('manual');
                  setEhrPopupStep(1);
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = themeMode === 'dark' ? '#404040' : '#d0d0d0';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = themeMode === 'dark' ? '#302d4c' : '#e9e8ea';
                }}
              >
                Cancel
              </button>
              {ehrPopupStep === 2 && (
              <button
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '5rem',
                  height: '2.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: currentTheme.callAutomationPage.activeButtonColor,
                  color: '#ffffff',
                }}
                onClick={() => {
                  // Validate EHR is selected
                  if (!selectedEhr) {
                    message.error('Please select an EHR system');
                    return;
                  }

                  // Get the configuration for this EHR and usecase
                  const ehrUsecaseConfig = ehrConfig?.[selectedEhr]?.[selectedUsecase];
                  if (!ehrUsecaseConfig) {
                    message.error('EHR configuration not available');
                    return;
                  }

                  if (!ehrUsecaseConfig.supported) {
                    message.error(`${ALL_EHRS.find((e) => e.key === selectedEhr)?.displayName || selectedEhr} is not configured for ${selectedUsecase.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}. Please select a different EHR system or use case.`);
                    return;
                  }

                  // Validate all required fields
                  const requiredFields = ehrUsecaseConfig.required_fields || [];
                  const missingFields = [];

                  if (!selectedEhrFacilityId) {
                    missingFields.push('facility_id');
                  }

                  for (const field of requiredFields) {
                    let value = null;
                    
                    if (field === 'start_date') value = ehrStartDate;
                    else if (field === 'end_date') value = ehrEndDate;
                    else value = ehrDynamicFields[field];
                    
                    if (!value || (Array.isArray(value) && value.length === 0)) {
                      missingFields.push(field);
                    }
                  }

                  if (missingFields.length > 0) {
                    const fieldNames = missingFields.map(f => 
                      f.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
                    ).join(', ');
                    message.error(`Please provide required field(s): ${fieldNames}`);
                    return;
                  }

                  setShowEhrConfigPopup(false);
                  setEhrPopupStep(1);
                  message.success('EHR configuration saved');
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '1';
                }}
              >
                Confirm
              </button>
              )}
            </div>
          </div>
          </div>
        </>
      )}
      
    </DataValidationOverlay>
  );
};
export default CallAutomation;
